<template>
  <div class="template-editor-container vh-100 d-flex flex-column"
       :class="{ 'is-preview': loadedType && isProtected }">
    <div class="d-flex align-items-center mx-2 my-1">
      <span class="mx-3 fs-5 fw-semibold">Template Editor</span>
      <!-- IMPORT and EXPORT are obsolete, if reenabled, the logic needs to be updated to follow load/save -->
      <!-- <FileUpload class="p-button-xs" chooseIcon="pi pi-upload" mode="basic" chooseLabel="Import" :customUpload="true"
        :auto="true" @uploader="importFile"
        :accept="'xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'" />
      <span class="p-1" />
      <Button class="p-button-xs" label="Export" @click="exportFile" />
      <span class="p-1" />
      <span v-if="fileName">{{ fileName }}</span>
      <span class="p-1" /> -->
      <Button class="p-button-xs p-button-info" label="Load RPK" icon="pi pi-download"
        :disabled="isLoading" @click="openPicker('RPK')" />
      <span class="p-1" />
      <Button class="p-button-xs p-button-info" label="Load CWK" icon="pi pi-download"
        :disabled="isLoading" @click="openPicker('CWK')" />
      <span class="p-1" />
      <span class="p-1" />
      <Button v-if="loadedType" class="p-button-xs p-button-success" label="Save" icon="pi pi-save"
        :disabled="isLoading" @click="saveSet" />
      <span class="p-1" />
      <Button v-if="loadedType" class="p-button-xs"
        :class="isProtected ? 'p-button-secondary' : 'p-button-warning'"
        :label="isProtected ? 'Preview Mode' : 'Design Mode'"
        :icon="isProtected ? 'pi pi-lock' : 'pi pi-pencil'"
        :disabled="isLoading" @click="toggleProtection" />
      <span class="p-1" />
      <span v-if="loadedLabel" class="text-muted">{{ loadedLabel }}</span>
      <TheLoading v-if="isLoading" />
    </div>
    <div class="container-fluid flex-grow-1 d-flex flex-column">
      <!-- Persistent #univer-container so the renderer survives sheetType
           switches; CWK adds a drag divider + collapse toggle. -->
      <div v-if="sheetType" class="flex-grow-1 d-flex position-relative">
        <div id="univer-container" class="h-100"
             :style="{ flexBasis: sheetType === 'cwk' && !navCollapsed ? `${100 - navWidthPct}%` : '100%' }"></div>
        <template v-if="sheetType === 'cwk'">
          <div v-show="!navCollapsed" class="template-editor-divider"
               @mousedown="startNavResize"></div>
          <div v-show="!navCollapsed" id="univer-container-nav" class="h-100"
               :style="{ flexBasis: `${navWidthPct}%` }"></div>
          <button type="button" class="template-editor-nav-toggle"
                  :title="navCollapsed ? 'Show RPK reference' : 'Hide RPK reference'"
                  @click="toggleNavCollapse">
            <i :class="navCollapsed ? 'pi pi-angle-double-left' : 'pi pi-angle-double-right'"></i>
          </button>
          <div v-if="pendingValueLink" class="template-editor-link-bar">
            <i class="pi pi-link"></i>
            <span class="fw-semibold">{{ pendingValueLink.sourceLabel }}</span>
            <i class="pi pi-arrow-left"></i>
            <span class="fw-semibold">{{ pendingValueLink.targetLabel }}</span>
            <span class="text-muted">Pick another RPK cell to change the target.</span>
            <Button class="p-button-xs p-button-success" label="Complete Link" icon="pi pi-check"
                    @click="confirmValueLink" />
            <Button class="p-button-xs p-button-text" label="Cancel" @click="cancelValueLink" />
          </div>
        </template>
      </div>
    </div>

    <TemplateSetPicker
      v-model:visible="pickerVisible"
      :sheetType="pickerType"
      :loadedType="loadedType"
      :loadedVersion="loadedVersion"
      @load="onPickerLoad"
    />
  </div>

  <!-- DATETIME picker: headerless, click-outside dismisses, auto-apply on pick. -->
  <Dialog v-model:visible="isDateTimePickerVisible"
          :showHeader="false" :dismissableMask="true" :modal="true"
          contentClass="p-2" style="width: auto;">
    <Calendar v-model="dateTimePickerValue" dateFormat="dd/mm/yy" inline
              @date-select="applyDateTimePick" />
  </Dialog>

  <!-- NUMBER options popup: default value + percentage / decimal-point formatting. -->
  <Dialog v-model:visible="isNumberPickerVisible"
          :showHeader="false" :dismissableMask="true" :modal="true"
          contentClass="p-2" style="width: auto;" @hide="cancelNumberPick">
    <div class="d-flex flex-column gap-3 p-2" style="min-width: 18rem;">
      <span class="fw-semibold">Set Number Options</span>

      <div class="d-flex flex-column gap-2">
        <div class="d-flex align-items-center">
          <RadioButton v-model="numberPickerMode" inputId="num-none" name="numDefault" value="none" />
          <label for="num-none" class="ms-2 mb-0">No default</label>
        </div>
        <div class="d-flex align-items-center gap-2">
          <RadioButton v-model="numberPickerMode" inputId="num-specific" name="numDefault" value="specific" />
          <label for="num-specific" class="mb-0">Specific value</label>
          <InputNumber v-model="numberPickerValue" placeholder="Enter default value"
                       class="flex-grow-1" :maxFractionDigits="10"
                       :disabled="numberPickerMode !== 'specific'"
                       @focus="numberPickerMode = 'specific'"
                       @keyup.enter="applyNumberPick" />
        </div>
      </div>

      <hr class="my-1" />

      <div class="d-flex align-items-center">
        <Checkbox v-model="numberPickerPercentage" inputId="num-pct" :binary="true" />
        <label for="num-pct" class="ms-2 mb-0">Percentage (%)</label>
      </div>
      <div class="d-flex align-items-center gap-2">
        <label for="num-dp" class="mb-0">Decimal points</label>
        <Dropdown inputId="num-dp" v-model="numberPickerDecimals" :options="numberDecimalOptions"
                  optionLabel="label" optionValue="value" class="flex-grow-1" />
      </div>

      <div class="d-flex justify-content-end gap-2">
        <Button label="Cancel" class="p-button-text p-button-sm" @click="cancelNumberPick" />
        <Button label="OK" class="p-button-sm" @click="applyNumberPick" />
      </div>
    </div>
  </Dialog>

  <!-- FORMULA / VALUE options popup: excel formula value + percentage / decimal-point formatting. -->
  <Dialog v-model:visible="isFormulaPickerVisible"
          :showHeader="false" :dismissableMask="true" :modal="true"
          contentClass="p-2" style="width: auto;" @hide="cancelFormulaPick">
    <div class="d-flex flex-column gap-3 p-2" style="min-width: 22rem;">
      <span class="fw-semibold">Set Options</span>

      <div class="d-flex flex-column gap-2">
        <label for="formula-input" class="mb-0">Value (formula)</label>
        <InputText id="formula-input" v-model="formulaPickerValue"
                   placeholder="=Sheet!A1 or =SUM(...)" class="w-100"
                   @keyup.enter="applyFormulaPick" />
        <div class="d-flex gap-2" v-if="formulaPickerCanLink">
          <Button label="Pick from RPK pane" icon="pi pi-link"
                  class="p-button-sm p-button-secondary"
                  @click="armFormulaFromPopup" />
        </div>
      </div>

      <hr class="my-1" />

      <div class="d-flex align-items-center">
        <Checkbox v-model="formulaPickerPercentage" inputId="formula-pct" :binary="true" />
        <label for="formula-pct" class="ms-2 mb-0">Percentage (%)</label>
      </div>
      <div class="d-flex align-items-center gap-2">
        <label for="formula-dp" class="mb-0">Decimal points</label>
        <Dropdown inputId="formula-dp" v-model="formulaPickerDecimals" :options="numberDecimalOptions"
                  optionLabel="label" optionValue="value" class="flex-grow-1" />
      </div>

      <div class="d-flex justify-content-end gap-2">
        <Button label="Cancel" class="p-button-text p-button-sm" @click="cancelFormulaPick" />
        <Button label="OK" class="p-button-sm" @click="applyFormulaPick" />
      </div>
    </div>
  </Dialog>

  <!-- Account / Translation popup: edits col B of the row it was opened on, never the selection. -->
  <Dialog v-model:visible="isAccountsDialogVisible" header="Account / Translation"
          modal style="width: 600px" @hide="resetAccountDialog">
    <div v-if="accountTargetLabel" class="text-muted small mb-3">{{ accountTargetLabel }}</div>
    <div class="row align-items-center mb-3">
      <label for="acc-list" class="col-md-3 col-form-label mb-0">Account</label>
      <div class="col-md-9">
        <Dropdown id="acc-list" class="w-100" v-model="selectedAccountRow" :options="accounts"
                  optionLabel="code" :filter="true" filterPlaceholder="Search code or description"
                  placeholder="Pick to fill code" :showClear="true" @change="onAccountPick">
          <template #value="{ value, placeholder }">
            <span v-if="value">{{ value.code }} — {{ value.description }}</span>
            <span v-else class="text-muted">{{ placeholder }}</span>
          </template>
          <template #option="{ option }">
            <span>{{ option.code }} — {{ option.description }}</span>
          </template>
        </Dropdown>
      </div>
    </div>
    <div class="row align-items-center mb-3">
      <label for="acc-code" class="col-md-3 col-form-label mb-0">Code</label>
      <div class="col-md-9">
        <InputText id="acc-code" class="w-100" v-model="accountCode" placeholder="e.g. 041409#" />
      </div>
    </div>
    <div class="row align-items-center">
      <label class="col-md-3 col-form-label mb-0">Translation</label>
      <div class="col-md-9">
        <Dropdown class="w-100" v-model="translationCode" :options="translationOptions" />
      </div>
    </div>

    <template #footer>
      <Button label="Cancel" class="p-button-text p-button-sm mr-2" @click="isAccountsDialogVisible = false" />
      <Button label="Insert" class="p-button-sm" :disabled="!canInsert" @click="applyAccountInsert" />
    </template>
  </Dialog>
</template>

<script>
import { markRaw } from 'vue';

import store from "@/store";

import { CellValueType, CommandType, IUndoRedoService, UniverInstanceType } from '@univerjs/core';
import { ContextMenuPosition, IMenuManagerService } from '@univerjs/ui';
import { IDescriptionService } from '@univerjs/sheets-formula';
import { BehaviorSubject, combineLatest, of, map } from 'rxjs';
import { UniverSheetsCorePreset, IDialogService } from '@univerjs/preset-sheets-core';
import UniverPresetSheetsCoreEnUS from '@univerjs/preset-sheets-core/locales/en-US';
import { UniverSheetsThreadCommentPreset } from '@univerjs/preset-sheets-thread-comment';
import UniverPresetSheetsThreadCommentEnUS from '@univerjs/preset-sheets-thread-comment/locales/en-US';
import { UniverSheetsDataValidationPreset } from '@univerjs/preset-sheets-data-validation';
import UniverPresetSheetsDataValidationEnUS from '@univerjs/preset-sheets-data-validation/locales/en-US';
import { UniverSheetsFindReplacePreset } from '@univerjs/preset-sheets-find-replace';
import UniverPresetSheetsFindReplaceEnUS from '@univerjs/preset-sheets-find-replace/locales/en-US';
import { createUniver, LocaleType, mergeLocales, defaultTheme } from '@univerjs/presets';
import '@univerjs/preset-sheets-core/lib/index.css';
import '@univerjs/preset-sheets-thread-comment/lib/index.css';
import '@univerjs/preset-sheets-data-validation/lib/index.css';
import '@univerjs/preset-sheets-find-replace/lib/index.css';
import './template-editor.css';

const LOCKED_MSG = 'This cell is locked. Switch to Design Mode to edit.';
// @univerjs/design's Dialog footer reads design.Confirm; the sheet bar's own name alerts pass
// button.cancel / button.confirm instead. Left alone the two render "cancel / ok" beside
// "Cancel / OK", so align design.Confirm with what button.* already says.
const DESIGN_LOCALE_OVERRIDE = {
  design: {
    Confirm: { cancel: 'Cancel', confirm: 'OK' },
  },
};
const PERMISSION_LOCALE_OVERRIDE = {
  permission: {
    dialog: {
      editErr: LOCKED_MSG, pasteErr: LOCKED_MSG, setStyleErr: LOCKED_MSG, copyErr: LOCKED_MSG,
      setRowColStyleErr: LOCKED_MSG, moveRowColErr: LOCKED_MSG, moveRangeErr: LOCKED_MSG,
      autoFillErr: LOCKED_MSG, formulaErr: LOCKED_MSG, hyperLinkErr: LOCKED_MSG, commentErr: LOCKED_MSG,
    },
  },
};

import LuckyExcel from '@mertdeveci55/univer-import-export';
import ExcelJS from 'exceljs';
import { FilterMatchMode } from 'primevue/api';

import { CWK_ACCOUNT_COLUMN, CWK_CR_COLUMN, CWK_DR_COLUMN, CWK_VALUE_COLUMN_LABEL, EDITABLE_COMPONENTS, LOCKED_COMPONENTS, applyBulkRules, applyComponent, cellHoldsFormula, columnBClaimsRow, drCrFormulaColumn, drCrLabel, isRowPlacementAllowed, DATETIME_SPECIFIC_SENTINEL, NUMBER_NO_DEFAULT, NUMBER_SPECIFIC_SENTINEL, buildNumberPattern, safeSetNumberFormat, getEditorMode, isValidPlacement, setEditorMode } from './component-rules';
import { eventBus } from './event-bus';
import { MERGE_HORIZONTAL_COMMAND, GoToLinkCellOperation, GoBackLinkCellOperation, clearLinkTrail } from './operation';
import { ComponentButtonsPlugin, ErrorDisplayPlugin } from './plugin';
import { buildWorkbookSnapshot, collectRuleCells, computeLockedRangesBySheet, extractWorkbookToDbJson } from './snapshot-builder';
import { cellContent, deleteComponent } from './utils';

import accountApi from "@/api/account-api";
import sheetApi from "@/api/sheet-api";
import templateApi from "@/api/template-api";
import formulaMixin from '@/mixin/formula';
import apiErrorMixin from '@/mixin/api-error';
import TemplateSetPicker from "./TemplateSetPicker.vue";
import TheLoading from "@/components/TheLoading.vue";

const HIDDEN_MENU_ITEMS = [
  'univer.command.undo',
  'univer.command.redo',
  'sheet.command.set-once-format-painter',
  'sheet.command.clear-selection-all',
  'sheet.command.set-range-stroke',
  'sheet.command.set-range-font-family',
  'sheet.command.set-range-fontsize',
  'sheet.command.set-range-font-increase',
  'sheet.command.set-range-font-decrease',
  'sheet.command.set-vertical-text-align',  
  'sheet.command.set-worksheet-col-width',
  'sheet.command.set-background-color',
  'sheet.command.set-text-wrap',
  'sheet.command.set-text-rotation',
  // Replaced by the Merge button; hiding the SUBITEMS parent takes its variants with it.
  // No unmerge entry: deleting the LONGLABEL/LONGTEXT component breaks the merge apart.
  'sheet.command.add-worksheet-merge',
  'sheet.command.add-worksheet-merge-vertical',
  'sheet.command.add-worksheet-merge-all',
  'sheet.command.remove-worksheet-merge',
  'sheet.command.numfmt.set.currency',
  'sheet.command.add-range-protection-from-toolbar',
  'base-ui.operation.toggle-shortcut-panel',
  'formula-ui.operation.insert-function',
  'formula-ui.operation.more-functions',
  'sheet.operation.open.numfmt.panel',
  'sheet.operation.toggle-comment-panel',
  'sheet.toolbar.text-to-number',
  'formula-ui.operation.insert-function.common',
  'formula-ui.operation.insert-function.financial',
  'formula-ui.operation.insert-function.logical',
  'formula-ui.operation.insert-function.text',
  'formula-ui.operation.insert-function.date',
  'formula-ui.operation.insert-function.lookup',
  'formula-ui.operation.insert-function.math',
  'formula-ui.operation.insert-function.statistical',
  'formula-ui.operation.insert-function.database',
  'formula-ui.operation.insert-function.engineering',
  'formula-ui.operation.insert-function.information',
  'sheet.contextMenu.permission',
  'thread-comment-ui.operation.toggle-panel',
  'sheet.menu.data-validation',
  'sheet.contextMenu.text-to-number',
  'sheet.command.copy-sheet',
  'sheet.command.set-worksheet-hidden',
  'sheet.command.set-worksheet-show',
  'ui-sheet.command.show-menu-list',
  'sheet.command.set-tab-color',
  'sheet.command.add-range-protection-from-sheet-bar',
  'sheet.command.delete-worksheet-protection-from-sheet-bar',
  'sheet.command.change-sheet-protection-from-sheet-bar',
  'sheet.command.view-sheet-permission-from-sheet-bar',
];

const HIDDEN_MENU = Object.fromEntries(
  HIDDEN_MENU_ITEMS.map((id) => [id, { hidden: true, disabled: true }])
);

// Univer formula autocomplete whitelist -- purely for suggestion display, so using an un-whitelisted 
// function still works. Empty to disable whitelisting. Limited to backend-supported functions.
const FORMULA_SUGGESTION_WHITELIST = [
  'SUM', 'AVERAGE', 'MIN', 'MAX', 'IF', 'COUNTIF', 'ROUND',
];

// A mutation only counts as an unsaved change if extractWorkbookToDbJson would read it back.
// Recalculation, edit-lock protection, the comments and validation rebuilt from component metadata
// on load, and the document unit behind the cell editor are all invisible to the saved payload.
const EXEMPT_MUTATION_PREFIXES = [
  'formula.mutation.', 'data-validation.mutation.', 'thread-comment.mutation.', 'doc.mutation.',
];
const EXEMPT_MUTATIONS = new Set([
  'sheet.mutation.add-range-protection',
  'sheet.mutation.set-range-protection',
  'sheet.mutation.delete-range-protection',
  'sheet.mutation.add-worksheet-protection',
  'sheet.mutation.set-worksheet-protection',
  'sheet.mutation.delete-worksheet-protection',
  'sheet.mutation.set-worksheet-permission-points',
  'sheet.mutation.set-worksheet-col-width',
  'sheet.mutation.set-worksheet-row-height',
  'sheet.mutation.set-worksheet-row-auto-height',
  'sheet.mutation.set-worksheet-row-is-auto-height',
  'sheet.mutation.data-validation-formula-mark-dirty',
  'sheet.mutation.mark-dirty-filter-change',
  'sheet.mutation.empty',
  'sheet.mutation.set-tab-color',
]);

// Undoing back to where a load or save left the stack restores that content - but loading is itself
// undoable so the clean depth is never zero, only a decrease counts (edits can climb back to a
// depth by another route), and past 20 entries the stack drops history and no depth means anything.
const UNDO_STACK_LIMIT = 20;

const LOCKED_CELL_BG = '#edededff';

// Grey tab for a sheet that is not live yet: added here but Closed until its template publishes.
// Worksheet tab colour is not part of what extractWorkbookToDbJson saves, so this cannot be
// mistaken for content.
const INACTIVE_TAB_COLOR = '#94a3b8';

// Grey italic marks the seeded sample row. This exact hex round-trips as var(--surface-400) through
// mapHexToPrimeVueColor, so the cue survives a save without drifting to a nearest-match colour.
const SAMPLE_FONT_COLOR = '#94a3b8';

const SHEET_NAME_MAX_LENGTH = 10;

// Puts a sheet-bar tab back into its inline rename editor.
const RENAME_SHEET_OPERATION = 'sheet.operation.rename-sheet';

const REMOVE_SHEET_COMMAND = 'sheet.command.remove-sheet';

// The sheet bar validates empty / special-char / duplicate names in the UI before the command
// dispatches, so those three never reach our interceptor. Ours borrow their title and phrasing.
// Title matches sheetConfig.sheetNameErrorTitle in @univerjs/sheets-ui.
const SHEET_NAME_ERROR_DIALOG = 'sheetNameInvalidAlert';
const SHEET_NAME_ERROR_TITLE = 'There was a problem';

const MAX_DECIMAL_PLACES = 4;

const TRANSLATION_OPTIONS = ['None', 'AV', 'DT', 'FX', 'MA', 'EDA'];

// Only these components have a spanning variant; merging anything else is refused.
const MERGE_PROMOTIONS = { LABEL: 'LONGLABEL', TEXT: 'LONGTEXT' };
const MERGE_DEMOTIONS = { LONGLABEL: 'LABEL', LONGTEXT: 'TEXT' };
const MERGEABLE_COMPONENTS = new Set(['LABEL', 'TEXT', 'LONGLABEL', 'LONGTEXT']);

const MERGE_VERTICAL_COMMAND = 'sheet.command.add-worksheet-merge-vertical';
const MERGE_COMMANDS = new Set([
  'sheet.command.add-worksheet-merge',
  'sheet.command.add-worksheet-merge-all',
  MERGE_HORIZONTAL_COMMAND,
  MERGE_VERTICAL_COMMAND,
]);
// Already split per row/column, so their merges need no row check.
const MERGE_SPLIT_COMMANDS = new Set([MERGE_HORIZONTAL_COMMAND, MERGE_VERTICAL_COMMAND]);
// Hooked on the mutations, not the commands, so undo/redo re-derive the tag for free.
const MERGE_MUTATION_ADD = 'sheet.mutation.add-worksheet-merge';
const MERGE_MUTATION_REMOVE = 'sheet.mutation.remove-worksheet-merge';
function decimalPlacesOfPattern(pattern) {
  const m = String(pattern || '').match(/\.([0]+)/);
  return m ? m[1].length : 0;
}

function formatDateDDMMYYYY(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

const IMPORT_DEFAULT_STYLE = {
  it: undefined,
  bl: undefined,
  st: undefined,
  ul: undefined,
  ol: undefined,
  tr: undefined,
  td: undefined,
  pd: undefined,
};

export default {
  components: { TemplateSetPicker, TheLoading },
  mixins: [formulaMixin, apiErrorMixin],
  data() {
    return {
      pageError: store.getters.error ?? '',
      univer: null,
      univerAPI: null,
      currentUnitId: null,
      univerData: null,
      fileName: '',
      sheetType: null,

      univerNav: null,
      univerAPINav: null,
      navUnitId: null,
      navNavigationDisposable: null,
      navLinkDisposable: null,
      autoSheetNameDisposable: null,
      armedValueCell: null,
      pendingValueLink: null,
      // univer sheet id -> { sheetId, templateId, isClosed } of the row it was loaded from. A tab
      // with no entry is new; an entry with no tab was deleted.
      sheetIdentity: {},

      pickerVisible: false,
      pickerType: 'RPK',
      isLoading: false,
      loadedLabel: '',
      loadedType: null,
      loadedVersion: null,
      isDirty: false,
      isProtected: true,
      lockedRulesBySheet: null,
      lockedRangesCache: null,

      navWidthPct: 50,
      navCollapsed: false,

      isDateTimePickerVisible: false,
      dateTimePickerValue: null,
      dateTimePickerRanges: null,

      isNumberPickerVisible: false,
      numberPickerValue: null,
      numberPickerRanges: null,
      numberPickerMode: 'specific',      // 'none' | 'specific'
      numberPickerPercentage: false,
      numberPickerDecimals: 'auto',
      numberDecimalOptions: [
        { label: 'Auto', value: 'auto' },
        { label: '0', value: 0 },
        { label: '1', value: 1 },
        { label: '2', value: 2 },
        { label: '3', value: 3 },
        { label: '4', value: 4 },
      ],

      isFormulaPickerVisible: false,
      formulaPickerRanges: null,
      formulaPickerComponent: null,      // 'FORMULA' | 'VALUE'
      formulaPickerValue: '=',
      formulaPickerPercentage: false,
      formulaPickerDecimals: 'auto',
      formulaPickerCanLink: false,       // VALUE cells can link from the RPK pane

      accounts: [],
      isAccountsDialogVisible: false,
      accountFilters: {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
      selectedAccountRow: null,
      accountCode: '',        // free-text account code written to the cell (verbatim, may keep '#')
      translationCode: 'None',
      translationOptions: TRANSLATION_OPTIONS,
      accountTargetRow: null, // col B row the popup was opened on; frozen so Insert can't drift
    };
  },
  computed: {
    canInsert() {
      return !!this.accountCode.trim() || this.translationCode !== 'None';
    },
    accountTargetLabel() {
      return this.accountTargetRow === null ? '' : `Row ${this.accountTargetRow + 1}`;
    },
  },
  mounted() {
    if (this.pageError === '') {
      accountApi.accountList({})
        .then((res) => {
          if (res?.success && res.data?.listSet) {
            this.accounts = res.data.listSet;
          }
        })
        .catch((error) => {
          this.toastApiError(error, 'Failed to load accounts');
        });
    }

    // eventBus is a module singleton, so these must come off again on unmount or a second
    // visit to the editor runs every handler twice.
    this._busHandlers = {
      'open-account-dialog': () => {
        this._openAccountDialogFromSelection();
      },
      'formula-refused': ({ row, col, skipped }) => {
        const more = skipped > 1 ? ` ${skipped} cell(s) skipped.` : '';
        this.$toast.add({
          severity: 'error', summary: 'Cannot insert formula',
          detail: `Row ${row + 1} has an account or translation in column B, so column `
            + `${drCrLabel(col)} is filled by the journal load. Clear column B first.${more}`,
          life: 5000,
        });
      },
      'value-refused': ({ skipped }) => {
        const more = skipped > 1 ? ` ${skipped} cell(s) skipped.` : '';
        this.$toast.add({
          severity: 'error', summary: 'Cannot insert value',
          detail: `A Value can only be placed in column ${CWK_VALUE_COLUMN_LABEL}. `
            + `Select cells in column ${CWK_VALUE_COLUMN_LABEL} and try again.${more}`,
          life: 5000,
        });
      },
      'arm-value-cell': ({ sheetId, cells }) => {
        if (this.sheetType !== 'cwk') return;
        const worksheet = this.univerAPI?.getActiveWorkbook()?.getSheetBySheetId(sheetId);
        if (!worksheet || !cells?.length) return;
        this._armValueCells(worksheet, cells);
      },
      'link-cell-none': () => {
        this.$toast.add({
          severity: 'warn', summary: 'No link',
          detail: 'This cell has no reference to another sheet. Select a cell whose formula points at one, then press F9.',
          life: 4000,
        });
      },
      'link-cell-missing': ({ name }) => {
        this.$toast.add({
          severity: 'error', summary: 'Sheet not found',
          detail: `This cell points at "${name}", which is not a sheet in this template set.`,
          life: 5000,
        });
      },
      'link-cell-no-history': () => {
        this.$toast.add({
          severity: 'warn', summary: 'Nothing to go back to',
          detail: 'Follow a link with F9 first; F10 then returns to the cell it started from.',
          life: 4000,
        });
      },
      'open-datetime-picker': ({ ranges }) => {
        this.dateTimePickerRanges = ranges;
        this.dateTimePickerValue = null;
        this.isDateTimePickerVisible = true;
      },
    };
    Object.entries(this._busHandlers).forEach(([event, handler]) => eventBus.on(event, handler));
    this._installTeardownErrorSilencer();
    this._dirtySeq = 0;
    this._takenCodes = new Set();
    this._undoDepth = 0;
    this._undoBaseline = 0;
    this._undoBaselineValid = false;
    this._beforeUnload = (e) => {
      if (!this.isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', this._beforeUnload);
  },
  beforeUnmount() {
    Object.entries(this._busHandlers || {}).forEach(([event, handler]) => eventBus.off(event, handler));
    this.disposeUniverInstances();
    this._uninstallTeardownErrorSilencer?.();
    this._undoStatusSub?.unsubscribe();
    if (this._beforeUnload) window.removeEventListener('beforeunload', this._beforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    // The editor opens in its own tab with no in-app navigation, so the only router push that
    // reaches this today is the 401 redirect - and holding that up strands a cleared session on a
    // page whose every save will fail.
    if (!this.isDirty || to.path === '/login') { next(); return; }
    // accept / reject / onHide are three routes out of one dialog; next() may only be called once.
    let settled = false;
    const answer = (leave) => {
      if (settled) return;
      settled = true;
      if (leave) next(); else next(false);
    };
    this.$confirm.require({
      header: 'Confirmation',
      message: 'You have unsaved changes that will be lost if you leave this page. Are you sure you want to continue?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => answer(true),
      reject: () => answer(false),
      onHide: () => answer(false),
    });
  },
  methods: {
    async createMainUniver() {
      const { univer, univerAPI } = await createUniver({
        id: 'main',
        locale: LocaleType.EN_US,
        locales: {
          [LocaleType.EN_US]: mergeLocales(
            UniverPresetSheetsCoreEnUS,
            UniverPresetSheetsThreadCommentEnUS,
            UniverPresetSheetsDataValidationEnUS,
            UniverPresetSheetsFindReplaceEnUS,
            PERMISSION_LOCALE_OVERRIDE,
            DESIGN_LOCALE_OVERRIDE,
          ),
        },
        theme: defaultTheme,
        presets: [
          UniverSheetsCorePreset({
            container: 'univer-container',
            menu: HIDDEN_MENU,
            ribbonType: 'simple',
            sheets: { protectedRangeShadow: 'none' },
          }),
          UniverSheetsThreadCommentPreset(),
          UniverSheetsDataValidationPreset({
            showSearchOnDropdown: false,
            showEditOnDropdown: false,
          }),
          UniverSheetsFindReplacePreset(),
        ],
        plugins: [ComponentButtonsPlugin],
      });
      // markRaw: Vue Proxies break Univer service lookups after disposeUnit+createUnit.
      this.univer = markRaw(univer);
      this.univerAPI = markRaw(univerAPI);

      this._installPermissionToast();
      this._installEventHandlers();
      this._watchUndoDepth();
      this.createNavigationMenus();
      this.createMenus();
    },

    // The same two commands the F9/F10 shortcuts run, on the right-click menu as well: the keys
    // are the fast path, the menu is the discoverable one (and the way out if a key is taken by
    // the browser or the OS). Unlike createMenus this applies to RPK too.
    createNavigationMenus() {
      if (this._navigationMenusInstalled) return;
      this._navigationMenusInstalled = true;
      const CONTEXT_MENU_PATH = ['contextMenu.mainArea', 'contextMenu.format'];

      this.univerAPI.createMenu({
        id: 'menu-go-to-link-cell',
        title: 'Go to link cell (F9)',
        order: -4,
        action: () => this.univerAPI.executeCommand(GoToLinkCellOperation.id),
      }).appendTo(CONTEXT_MENU_PATH);

      this.univerAPI.createMenu({
        id: 'menu-go-back-link-cell',
        title: 'Go back to last sheet (F10)',
        order: -3,
        action: () => this.univerAPI.executeCommand(GoBackLinkCellOperation.id),
      }).appendTo(CONTEXT_MENU_PATH);
    },

    // Drop every function description outside the whitelist. SHEETS_FORMULA_PLUGIN is scoped to
    // UNIVER_SHEET, so IDescriptionService only exists once a sheet unit does - hence the load-path
    // call site rather than createMainUniver. Re-running is a no-op: nothing outside it is left.
    _installFormulaSuggestionFilter() {
      if (!FORMULA_SUGGESTION_WHITELIST.length) return;
      try {
        const descriptionService = this.univerAPI._injector.get(IDescriptionService);
        const keep = new Set(FORMULA_SUGGESTION_WHITELIST);
        const drop = Array.from(descriptionService.getDescriptions().keys())
          .filter((name) => !keep.has(name));
        if (drop.length) descriptionService.unregisterDescriptions(drop);
      } catch (err) {
        // Cosmetic filter - never let it take the template load down with it.
        console.warn('[formula-filter] skipped', err);
      }
    },

    _isUnsavedChange(e) {
      if (e.type !== CommandType.MUTATION) return false;
      // The engine writes calculated results back through set-range-values, so the id alone cannot
      // separate them from a keystroke. Univer tags those executions and screens them out of its
      // own edit tracking the same way (@univerjs/sheets-ui).
      if (e.options?.fromCollab || e.options?.fromSync || e.options?.fromFormula) return false;
      return !EXEMPT_MUTATIONS.has(e.id)
        && !EXEMPT_MUTATION_PREFIXES.some((prefix) => e.id.startsWith(prefix));
    },

    _markDirty() {
      this._dirtySeq += 1;
      this.isDirty = true;
    },

    _watchUndoDepth() {
      const undoRedo = this.univer.__getInjector().get(IUndoRedoService);
      this._undoStatusSub = undoRedo.undoRedoStatus$.subscribe(({ undos }) => {
        const undone = undos < this._undoDepth;
        this._undoDepth = undos;
        if (undos >= UNDO_STACK_LIMIT) this._undoBaselineValid = false;
        if (undone && undos === this._undoBaseline && this._undoBaselineValid) this.isDirty = false;
      });
    },

    // Disposing a Univer instance completes its hover stream, and debounceTime flushes whatever it
    // was still holding - into plugin subscribers that dereference the unit dispose just removed.
    // Thrown from rxjs's own setTimeout, so no try/catch around dispose() can reach it.
    _installTeardownErrorSilencer() {
      const swallow = (e) => {
        const msg = String(e.message || e.reason?.message || e.reason || '');
        const isTeardownNoise = /\[redi\][\s\S]*disposed/i.test(msg)
          || /reading 'getSheetBySheetId'/.test(msg);
        if (isTeardownNoise) e.preventDefault();
      };
      window.addEventListener('error', swallow);
      window.addEventListener('unhandledrejection', swallow);
      this._uninstallTeardownErrorSilencer = () => {
        window.removeEventListener('error', swallow);
        window.removeEventListener('unhandledrejection', swallow);
      };
    },

    _installPermissionToast() {
      const PERMISSION_DIALOG_IDS = new Set([
        'UNIVER_SHEET_PERMISSION_DIALOG_ID',
        'UNIVER_SHEET_PERMISSION_USER_DIALOG_ID',
      ]);
      const originalOpenDialog = this.univerAPI.openDialog.bind(this.univerAPI);
      this.univerAPI.openDialog = (dialog) => {
        if (dialog?.id && PERMISSION_DIALOG_IDS.has(dialog.id)) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Locked',
            detail: LOCKED_MSG,
            life: 3000,
          });
          // eslint-disable-next-line
          return { dispose() {} };
        }
        return originalOpenDialog(dialog);
      };
    },

    _installEventHandlers() {
      const Event = this.univerAPI.Event;

      // Clipboard/drag-fill/drag-move drop custom.component — snapshot before, replay after.
      this._pendingMetaReplay = null;
      this.univerAPI.addEvent(Event.BeforeClipboardChange, () => {
        const src = this._getActiveRange();
        if (src) this._pendingMetaReplay = { sourceGrid: this._captureMeta(src) };
      });
      this.univerAPI.addEvent(Event.ClipboardPasted, () => {
        const target = this._getActiveRange();
        if (this._pendingMetaReplay) {
          this._pendingMetaReplay.target = target;
          this._replayMeta(this._pendingMetaReplay);
          return;
        }
        // Pasted from outside the editor, so there is no captured metadata to replay - but the
        // row rules still have to hold, and this is the only event that path raises.
        this._enforceRowRules(this.univerAPI.getActiveWorkbook()?.getActiveSheet(), target);
      });

      const beforeHandlers = {
        'sheet.command.auto-fill': (e) => {
          this._pendingMetaReplay = {
            sourceGrid: this._captureMeta(e.params?.sourceRange),
            target: e.params?.targetRange,
          };
        },
        'sheet.command.move-range': (e) => {
          this._pendingMetaReplay = {
            sourceGrid: this._captureMeta(e.params?.fromRange),
            target: e.params?.toRange,
            clearSource: e.params?.fromRange,
          };
        },
        // Design = full delete, Preview = value-only. Guard breaks deleteComponent re-entry.
        'sheet.command.clear-selection-content': (e) => {
          if (this._inComponentDelete) return;
          e.cancel = true;
          const fSheet = this.univerAPI.getActiveWorkbook()?.getActiveSheet();
          if (!fSheet) return;
          const fRanges = fSheet.getSelection()?.getActiveRangeList() || [];
          if (this.isProtected) {
            fRanges.forEach((fr) => fr.setValue(''));
          } else {
            this._inComponentDelete = true;
            try { fRanges.forEach((fr) => deleteComponent(fr)); }
            finally { this._inComponentDelete = false; }
          }
        },
        'sheet.command.set-worksheet-name': (e) => {
          const name = (e.params?.name ?? '').trim();
          if (!name) {
            this._rejectRename(e, 'The sheet name cannot be empty.');
          } else if (name.length > SHEET_NAME_MAX_LENGTH) {
            this._rejectRename(e, `The sheet name cannot exceed ${SHEET_NAME_MAX_LENGTH} characters.`
              + ' Please enter another name.');
          } else if (this._takenCodes.has(name)) {
            this._rejectRename(e, `The sheet name "${name}" is already used by another sheet.`
              + ' Please enter another name.');
          }
        },
        'sheet.command.remove-sheet-confirm': (e) => this._guardRemoveSheet(e),
        [REMOVE_SHEET_COMMAND]: (e) => this._guardRemoveSheet(e),
        'sheet.command.insert-sheet': (e) => {
          if (!this.isProtected) return;
          e.cancel = true;
          this.$toast.add({
            severity: 'error', summary: 'Error',
            detail: 'Cannot add sheets in Preview mode', life: 3000,
          });
        },
        'sheet.command.numfmt.set.numfmt': (e) => {
          const tooMany = (e.params?.values || []).some(
            (v) => decimalPlacesOfPattern(v.pattern) > MAX_DECIMAL_PLACES,
          );
          if (tooMany) e.cancel = true;
        },
        'sheet.command.numfmt.add.decimal.command': (e) => {
          if (this._selectionAtDecimalCap()) e.cancel = true;
        },
      };
      this.univerAPI.addEvent(Event.BeforeCommandExecute, (e) => {
        if (MERGE_COMMANDS.has(e.id)) {
          // mergeAcross merges one range at a time, so judge what the command was handed.
          const ranges = e.params?.selections || this._activeRanges();
          if (!this._canMergeSelection(ranges)) {
            e.cancel = true;
            return;
          }
          // Always horizontal: the grid JSON stores a colspan and no rowspan. The per-row
          // variant carries `value`, so this cannot recurse.
          const splitsPerRow = MERGE_SPLIT_COMMANDS.has(e.id) || e.params?.value !== undefined;
          if (!splitsPerRow && ranges.some((r) => r.endRow > r.startRow)) {
            e.cancel = true;
            Promise.resolve().then(() => this.univerAPI.executeCommand(MERGE_HORIZONTAL_COMMAND));
            return;
          }
        }
        beforeHandlers[e.id]?.(e);
      });

      // Typing blocked on tokens; paste replacement still works. CWK col B routes to the Account
      // dialog in Design Mode. Double-click / F2 / type on NUMBER, FORMULA or VALUE (design mode)
      // opens the options popup instead of the cell editor; single-click keeps normal selection.
      this.univerAPI.addEvent(Event.BeforeSheetEditStart, (params) => {
        const cell = params.worksheet.getRange(params.row, params.column);
        const component = cell.getCustomMetaData()?.component;
        // Design Mode only: in Preview the range protection owns col B, and cancelling here
        // would swallow the locked-cell alert it raises for every other locked cell.
        const inCwkColB = !this.isProtected && getEditorMode() === 'cwk'
          && params.column === CWK_ACCOUNT_COLUMN;
        if (inCwkColB) {
          params.cancel = true;
          // Double-click a col B cell opens the Account / Translation popup, pre-filled.
          this._openAccountDialog(params.worksheet, params.row);
          return;
        }
        if (component && LOCKED_COMPONENTS.has(component)) {
          params.cancel = true;
          return;
        }
        // Col B claims the row, so its DR/CR formula is legacy: read-only, not editable in place.
        if (!this.isProtected && component === 'FORMULA'
            && !isRowPlacementAllowed(params.worksheet, params.row, params.column, 'FORMULA')) {
          params.cancel = true;
          this.$toast.add({
            severity: 'error', summary: 'Cannot edit formula',
            detail: `Row ${params.row + 1} has an account or translation in column B, so this `
              + 'formula is overwritten by the journal load. Press Delete to remove it.',
            life: 5000,
          });
          return;
        }
        if (!this.isProtected && (component === 'NUMBER' || component === 'FORMULA' || component === 'VALUE')) {
          params.cancel = true;
          // DeviceInputEventType.Dblclick === 3 (@univerjs/engine-render). Double-click still
          // opens the options dialog; the fx toolbar (any other trigger) arms VALUE cells.
          const isDblclick = params.eventType === 3;
          if (component === 'NUMBER') {
            this._openNumberOptions(params.worksheet, params.row, params.column);
          } else if (component === 'VALUE' && this.univerAPINav && !isDblclick) {
            // VALUE (RPK link) via the fx toolbar: arm the cell directly instead of opening the
            // Set Options dialog, so the user can click any cell in the RPK split screen to (re)link.
            this._armValueCell(params.worksheet, params.row, params.column);
          } else if (component === 'FORMULA' && !isDblclick) {
            // FORMULA: only double-click opens the Set Options dialog; the fx toolbar does
            // nothing (the edit is already cancelled above).
          } else {
            this._openFormulaOptions(params.worksheet, params.row, params.column, component);
          }
        }
      });

      const REPLAY_COMMANDS = new Set([
        'sheet.command.auto-fill',
        'sheet.command.move-range',
      ]);
      this.univerAPI.addEvent(Event.CommandExecuted, (e) => {
        if (this._isUnsavedChange(e)) this._markDirty();
        if (REPLAY_COMMANDS.has(e.id) && this._pendingMetaReplay) {
          this._replayMeta(this._pendingMetaReplay);
          this._pendingMetaReplay = null;
        }
        if (e.id === 'sheet.command.set-range-values') {
          this._autoTagTypedValue(e.params?.range);
          this._handleDateTimeSentinel(e.params?.range);
        } else if (e.id === MERGE_MUTATION_ADD) {
          this._promoteMerged(e.params?.subUnitId, e.params?.ranges);
        } else if (e.id === MERGE_MUTATION_REMOVE) {
          this._demoteUnmerged(e.params?.subUnitId, e.params?.ranges);
        } else if (e.id === REMOVE_SHEET_COMMAND) {
          this._refreshSheetStatus();
        }
      });

      this.univerAPI.addEvent(Event.SheetCreated, ({ worksheet }) => {
        worksheet.setColumnWidth(0, 400);
        worksheet.setColumnWidths(1, worksheet.getMaxColumns() - 1, 120);
        worksheet.setRowHeights(0, worksheet.getMaxRows(), 20);
      });

      let lastKey = '';
      this.univerAPI.addEvent(Event.CellHover, ({ worksheet, row, column }) => {
        const key = `${row}:${column}`;
        if (key === lastKey) return;
        lastKey = key;
        const range = worksheet.getRange(row, column);
        const meta = range.getCustomMetaData();
        const container = document.querySelector('[id^="univer-sheet-main-canvas"]');
        const cellName = range.getA1Notation();
        container.title = meta?.component ? `${cellName} ${meta.component}` : '';
      });
    },
    async createNavUniver() {
      const { univer, univerAPI } = await createUniver({
        id: 'nav',
        locale: LocaleType.EN_US,
        locales: {
          [LocaleType.EN_US]: mergeLocales(
            UniverPresetSheetsCoreEnUS,
            UniverPresetSheetsThreadCommentEnUS,
            UniverPresetSheetsDataValidationEnUS,
            UniverPresetSheetsFindReplaceEnUS,
            PERMISSION_LOCALE_OVERRIDE,
            DESIGN_LOCALE_OVERRIDE,
          ),
        },
        theme: defaultTheme,
        presets: [
          UniverSheetsCorePreset({
            container: 'univer-container-nav',
            toolbar: false,
          }),
          UniverSheetsThreadCommentPreset(),
          UniverSheetsDataValidationPreset({
            showSearchOnDropdown: false,
            showEditOnDropdown: false,
          }),
          UniverSheetsFindReplacePreset(),
        ],
        plugins: [ErrorDisplayPlugin],
      });
      this.univerNav = markRaw(univer);
      this.univerAPINav = markRaw(univerAPI);
    },
    // Reuse instance across swaps; full dispose+recreate breaks LocaleService.
    async _swapMainUnit(snapshot) {
      if (!this.univer) {
        await this.createMainUniver();
      } else if (this.currentUnitId) {
        this._pendingMetaReplay = null;
        this.lockedRulesBySheet = null;
        this.lockedUnitId = null;
        this._clearValueArm();
        this.univerAPI.disposeUnit(this.currentUnitId);
        this.currentUnitId = null;
        await this.$nextTick();
      }
      clearLinkTrail();
      this.univer.createUnit(UniverInstanceType.UNIVER_SHEET, snapshot);
      this.currentUnitId = snapshot.id;
      // Snapshot `f` cells don't auto-recalc on a reused instance.
      this.univerAPI.getFormula()?.executeCalculation();
    },
    async _swapNavUnit(snapshot) {
      if (!this.univerNav) await this.createNavUniver();
      if (this.navUnitId) {
        this._clearValueArm();
        this.univerAPINav.disposeUnit(this.navUnitId);
        this.navUnitId = null;
        await this.$nextTick();
      }
      this.univerNav.createUnit(UniverInstanceType.UNIVER_SHEET, snapshot);
      this.navUnitId = snapshot.id;
      this.univerAPINav.getFormula()?.executeCalculation();
    },
    disposeUniverInstances() {
      if (this.univer) {
        this.univer.dispose();
        this.univer = null;
        this.univerAPI = null;
      }
      this.disposeNavInstance();
    },
    disposeNavInstance() {
      ['navNavigationDisposable', 'navLinkDisposable'].forEach((key) => {
        this[key]?.dispose();
        this[key] = null;
      });
      this._clearValueArm();
      if (this.univerNav) {
        this.univerNav.dispose();
        this.univerNav = null;
        this.univerAPINav = null;
        this.navUnitId = null;
      }
    },
    async applyEditLock(unitId, lockedRanges) {
      if (this.isProtected) await this._installProtection(unitId, lockedRanges);
    },

    async _installProtection(unitId, lockedRanges) {
      const fWorkbook = this.univerAPI.getActiveWorkbook();
      if (!fWorkbook) return;
      const permission = fWorkbook.getPermission();
      const { RangeProtectionPermissionEditPoint } = permission.permissionPointsDefinition;

      const bySheet = new Map();
      lockedRanges.forEach((lr) => {
        const list = bySheet.get(lr.sheetId) || [];
        list.push(lr);
        bySheet.set(lr.sheetId, list);
      });

      const installs = Array.from(bySheet.entries()).map(async ([sheetId, ranges]) => {
        if (this.currentUnitId !== unitId) return null;
        const fWorksheet = fWorkbook.getSheetBySheetId(sheetId);
        if (!fWorksheet) return null;
        const fRanges = ranges.map((r) => fWorksheet.getRange(
          r.startRow, r.startColumn,
          r.endRow - r.startRow + 1,
          r.endColumn - r.startColumn + 1,
        ));
        try {
          const res = await permission.addRangeBaseProtection(unitId, sheetId, fRanges);
          if (!res) return null;
          permission.setRangeProtectionPermissionPoint(
            unitId, sheetId, res.permissionId,
            RangeProtectionPermissionEditPoint, false,
          );
          return [sheetId, res.ruleId];
        } catch {
          return null; // unit disposed mid-await
        }
      });
      const results = await Promise.all(installs);
      if (this.currentUnitId !== unitId) return;
      this.lockedRulesBySheet = new Map(results.filter(Boolean));
      this.lockedUnitId = unitId;
    },

    // Wrap each item's hidden$ with the Preview flag - wrapping, not replacing, keeps the
    // hidden$/disabled$/labels Design Mode needs; merging by id covers main area and both headers.
    _lockMenuItemsInPreview(targetIds) {
      // Lazily created: createMenus() (Univer setup) and _installPreviewMenuLocks() (template-set
      // load) can each run first. markRaw as for univerAPI - a Vue Proxy breaks rxjs internals.
      if (!this._previewLocked$) this._previewLocked$ = markRaw(new BehaviorSubject(this.isProtected));
      const menuManagerService = this.univerAPI._injector.get(IMenuManagerService);
      const captured = new Map();
      [
        ContextMenuPosition.MAIN_AREA,
        ContextMenuPosition.ROW_HEADER,
        ContextMenuPosition.COL_HEADER,
      ].forEach((position) => {
        menuManagerService.getFlatMenuByPositionKey(position).forEach((node) => {
          if (node.item && targetIds.includes(node.key) && !captured.has(node.key)) {
            captured.set(node.key, node.item);
          }
        });
      });

      const overrides = {};
      captured.forEach((item, key) => {
        const hidden$ = combineLatest([item.hidden$ || of(false), this._previewLocked$])
          .pipe(map(([builtIn, previewLocked]) => builtIn || previewLocked));
        overrides[key] = { menuItemFactory: () => ({ ...item, hidden$ }) };
      });
      if (Object.keys(overrides).length) menuManagerService.mergeMenu(overrides);
    },

    // Preview must never offer Insert/Delete, but Univer only hides them when the selection
    // intersects a protected range, and Preview leaves input cells unprotected so they stay fillable.
    _installPreviewMenuLocks() {
      if (this._previewMenuLocksInstalled) return;
      this._previewMenuLocksInstalled = true;
      this._lockMenuItemsInPreview(['sheet.menu.cell-insert', 'sheet.menu.delete']);
    },

    _removeProtection() {
      if (!this.lockedRulesBySheet || !this.lockedUnitId) return;
      const fWorkbook = this.univerAPI.getActiveWorkbook();
      if (!fWorkbook) return;
      const permission = fWorkbook.getPermission();
      for (const [sheetId, ruleId] of this.lockedRulesBySheet) {
        permission.removeRangeProtection(this.lockedUnitId, sheetId, [ruleId]);
      }
      this.lockedRulesBySheet = null;
      this.lockedUnitId = null;
    },

    // Recompute from live state so design-mode edits are honoured on re-lock.
    computeLockedRangesFromWorkbook() {
      const fWorkbook = this.univerAPI.getActiveWorkbook();
      if (!fWorkbook) return [];
      const out = [];
      for (const fWorksheet of fWorkbook.getSheets()) {
        const sheetId = fWorksheet.getSheetId();
        const rowCount = fWorksheet.getMaxRows();
        const colCount = fWorksheet.getMaxColumns();
        for (let r = 0; r < rowCount; r++) {
          let spanStart = -1;
          for (let c = 0; c < colCount; c++) {
            const meta = fWorksheet.getRange(r, c).getCustomMetaData();
            // Tokens replaceable; typed edits blocked elsewhere. CWK col B is template layout,
            // not user input, so Preview locks it like any other LABEL.
            const editable = meta?.component && (
              EDITABLE_COMPONENTS.has(meta.component) || LOCKED_COMPONENTS.has(meta.component)
            );
            if (!editable) {
              if (spanStart < 0) spanStart = c;
            } else if (spanStart >= 0) {
              out.push({ sheetId, startRow: r, endRow: r, startColumn: spanStart, endColumn: c - 1 });
              spanStart = -1;
            }
          }
          if (spanStart >= 0) {
            out.push({ sheetId, startRow: r, endRow: r, startColumn: spanStart, endColumn: colCount - 1 });
          }
        }
      }
      return out;
    },

    // = → FORMULA/VALUE, number → NUMBER, else → LABEL. Skips already-tagged cells.
    _autoTagTypedValue(range) {
      const fWorksheet = this.univerAPI.getActiveWorkbook()?.getActiveSheet();
      if (!fWorksheet) return;
      const r0 = range?.startRow, c0 = range?.startColumn;
      const r1 = range?.endRow, c1 = range?.endColumn;
      if (r0 == null || c0 == null || r1 == null || c1 == null) return;
      for (let r = r0; r <= r1; r++) {
        for (let c = c0; c <= c1; c++) {
          const cell = fWorksheet.getRange(r, c);
          const value = cell.getValue();
          const formula = cell.getFormula();
          const component = cell.getCustomMetaData()?.component;
          if ((value == null || value === '') && !formula) continue;
          const isFormula = !!formula || (typeof value === 'string' && value.startsWith('='));
          const isNumber = !formula && typeof value === 'number' && Number.isFinite(value);
          if (isFormula && (!component || component === 'LABEL')) {
            const formulaComp = isValidPlacement('FORMULA', c) ? 'FORMULA' : 'VALUE';
            applyComponent(this.univerAPI, cell, formulaComp, true);
          } else if (isNumber && !component) {
            applyComponent(this.univerAPI, cell, 'NUMBER', true);
          } else if (!isFormula && !isNumber && !component) {
            applyComponent(this.univerAPI, cell, 'LABEL', true);
          }
        }
      }
      this._enforceRowRules(fWorksheet, range);
    },
    // Paste, drag-fill and move-range commit through mutations, so neither the toolbar guard nor
    // the auto-tagger above ever sees them - every write path sweeps its own range instead.
    // Only cells inside `range` are cleared, so a write never destroys the side that was already there.
    _enforceRowRules(fWorksheet, range) {
      // setValue re-enters this sweep through the set-range-values listener, so guard the way
      // the clear-selection-content handler does.
      if (this._inRowRuleSweep) return;
      if (this.sheetType !== 'cwk' || !fWorksheet || !range) return;
      const r0 = range.startRow, r1 = range.endRow;
      const c0 = range.startColumn, c1 = range.endColumn;
      if (r0 == null || c0 == null || r1 == null || c1 == null) return;

      let clearedFormula = 0;
      let clearedColumnB = 0;
      this._inRowRuleSweep = true;
      try {
        for (let r = r0; r <= r1; r++) {
          if (!columnBClaimsRow(fWorksheet, r) || drCrFormulaColumn(fWorksheet, r) === -1) continue;
          const written = [CWK_DR_COLUMN, CWK_CR_COLUMN].filter(
            (c) => c >= c0 && c <= c1 && cellHoldsFormula(fWorksheet.getRange(r, c)),
          );
          for (const c of written) {
            const cell = fWorksheet.getRange(r, c);
            // Tag first: cellHoldsFormula reads it before the value, and setValue('') leaves it.
            cell.setCustomMetaData(null);
            cell.setValue('');
            clearedFormula += 1;
          }
          // Col B only loses out when the write did not bring the formula with it.
          if (!written.length && CWK_ACCOUNT_COLUMN >= c0 && CWK_ACCOUNT_COLUMN <= c1) {
            fWorksheet.getRange(r, CWK_ACCOUNT_COLUMN).setValue('');
            clearedColumnB += 1;
          }
        }
      } finally {
        this._inRowRuleSweep = false;
      }

      if (clearedFormula) {
        this.$toast.add({
          severity: 'error', summary: 'Formula removed',
          detail: 'Column D (DR) and E (CR) are filled by the journal load when column B has an '
            + `account or translation. ${clearedFormula} cell(s) cleared.`,
          life: 5000,
        });
      }
      if (clearedColumnB) {
        this.$toast.add({
          severity: 'error', summary: 'Column B cleared',
          detail: 'A row with a formula in column D (DR) or E (CR) cannot hold an account or '
            + `translation. ${clearedColumnB} cell(s) cleared.`,
          life: 5000,
        });
      }
    },
    _handleDateTimeSentinel(range) {
      if (!range) return;
      const fWorksheet = this.univerAPI.getActiveWorkbook()?.getActiveSheet();
      if (!fWorksheet) return;
      for (let r = range.startRow; r <= range.endRow; r++) {
        for (let c = range.startColumn; c <= range.endColumn; c++) {
          const cell = fWorksheet.getRange(r, c);
          if (cell.getCustomMetaData()?.component !== 'DATETIME') continue;
          if (cell.getValue() !== DATETIME_SPECIFIC_SENTINEL) continue;
          const today = new Date();
          cell.setValue(formatDateDDMMYYYY(today));
          this.dateTimePickerRanges = [{ startRow: r, endRow: r, startColumn: c, endColumn: c }];
          this.dateTimePickerValue = today;
          this.isDateTimePickerVisible = true;
          return;
        }
      }
    },
    // Open the Set Number Options popup for a NUMBER cell (default value + percentage /
    // decimal formatting), pre-filled from the cell's current value and number format.
    _openNumberOptions(fWorksheet, row, column) {
      if (!fWorksheet) return;
      const cell = fWorksheet.getRange(row, column);
      if (cell.getCustomMetaData()?.component !== 'NUMBER') return;

      const pattern = cell.getCellStyleData()?.n?.pattern || '';
      this.numberPickerPercentage = pattern.includes('%');
      this.numberPickerDecimals = pattern ? decimalPlacesOfPattern(pattern) : 'auto';

      const current = cell.getValue();
      const n = Number(current);
      if (current == null || current === '' || current === NUMBER_NO_DEFAULT || Number.isNaN(n)) {
        this.numberPickerMode = 'none';
        this.numberPickerValue = null;
      } else {
        this.numberPickerMode = 'specific';
        this.numberPickerValue = n;
      }
      this.numberPickerRanges = [{ startRow: row, endRow: row, startColumn: column, endColumn: column }];
      this.isNumberPickerVisible = true;
    },
    _activeRanges() {
      return (this.univerAPI.getActiveWorkbook()?.getActiveSheet()?.getSelection()
        ?.getActiveRangeList() || []).map((fRange) => fRange.getRange());
    },
    // Undo replays the remove mutation; without this the anchor keeps a colspan-less LONG*.
    _demoteUnmerged(subUnitId, ranges) {
      const fWorksheet = this.univerAPI.getActiveWorkbook()?.getSheetBySheetId(subUnitId);
      if (!fWorksheet) return;
      for (const m of ranges || []) {
        const anchor = fWorksheet.getRange(m.startRow, m.startColumn);
        const component = MERGE_DEMOTIONS[anchor.getCustomMetaData()?.component];
        if (component) anchor.setCustomMetaData({ component });
      }
    },
    // Anchor only — extraction nulls the covered cells. One-column merges have no colspan to
    // store, and a LONG* without one reloads spanning the row.
    _promoteMerged(subUnitId, ranges) {
      const fWorksheet = this.univerAPI.getActiveWorkbook()?.getSheetBySheetId(subUnitId);
      if (!fWorksheet) return;
      for (const m of ranges || []) {
        if (m.endColumn <= m.startColumn) continue;
        const anchor = fWorksheet.getRange(m.startRow, m.startColumn);
        const current = anchor.getCustomMetaData()?.component;
        const component = current ? MERGE_PROMOTIONS[current] : 'LONGLABEL';
        if (!component || !isValidPlacement(component, m.startColumn)) continue;
        anchor.setCustomMetaData({ component });
      }
    },
    // Untagged cells stay mergeable — that is how a LONGLABEL is authored from blanks.
    _canMergeSelection(ranges) {
      const fWorksheet = this.univerAPI.getActiveWorkbook()?.getActiveSheet();
      if (!fWorksheet) return false;
      for (const r of ranges) {
        // Refuse what _promoteMerged could not tag: a merge anchored in a column that rejects the
        // LONG* would commit untagged, lose its colspan on save and blank the cells it covered.
        const anchor = fWorksheet.getRange(r.startRow, r.startColumn).getCustomMetaData()?.component;
        const promoted = anchor ? MERGE_PROMOTIONS[anchor] : 'LONGLABEL';
        if (promoted && !isValidPlacement(promoted, r.startColumn)) {
          this.$toast.add({
            severity: 'warn', summary: 'Cannot merge',
            detail: 'This column cannot hold a merged Label or Text.', life: 3000,
          });
          return false;
        }
        for (let row = r.startRow; row <= r.endRow; row++) {
          for (let column = r.startColumn; column <= r.endColumn; column++) {
            const component = fWorksheet.getRange(row, column).getCustomMetaData()?.component;
            if (component && !MERGEABLE_COMPONENTS.has(component)) {
              this.$toast.add({
                severity: 'warn', summary: 'Cannot merge',
                detail: `Only Label and Text cells can be merged (found ${component}).`, life: 3000,
              });
              return false;
            }
          }
        }
      }
      return true;
    },
    startNavResize(e) {
      e.preventDefault();
      const row = e.currentTarget.parentElement;
      const onMove = (ev) => {
        const rect = row.getBoundingClientRect();
        const leftPct = ((ev.clientX - rect.left) / rect.width) * 100;
        this.navWidthPct = Math.max(10, Math.min(90, 100 - leftPct));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    toggleNavCollapse() {
      this.navCollapsed = !this.navCollapsed;
      this.$nextTick(() => window.dispatchEvent(new Event('resize')));
    },
    _selectionAtDecimalCap() {
      const fSheet = this.univerAPI.getActiveWorkbook()?.getActiveSheet();
      const ranges = fSheet?.getSelection()?.getActiveRangeList() || [];
      return ranges.some((fr) => {
        const r = fr.getRange();
        for (let row = r.startRow; row <= r.endRow; row++) {
          for (let col = r.startColumn; col <= r.endColumn; col++) {
            const pattern = fSheet.getRange(row, col).getCellStyleData()?.n?.pattern;
            if (decimalPlacesOfPattern(pattern) >= MAX_DECIMAL_PLACES) return true;
          }
        }
        return false;
      });
    },
    _getActiveRange() {
      const ranges = this.univerAPI.getActiveWorkbook()
        ?.getActiveSheet()?.getSelection()?.getActiveRangeList();
      return ranges?.[0]?.getRange() || null;
    },

    _captureMeta(range) {
      if (!range) return null;
      const fWorksheet = this.univerAPI.getActiveWorkbook()?.getActiveSheet();
      if (!fWorksheet) return null;
      const grid = [];
      for (let r = range.startRow; r <= range.endRow; r++) {
        const row = [];
        for (let c = range.startColumn; c <= range.endColumn; c++) {
          row.push(fWorksheet.getRange(r, c).getCustomMetaData()?.component ?? null);
        }
        grid.push(row);
      }
      return grid;
    },

    async _replayMeta({ sourceGrid, target, clearSource }) {
      if (!sourceGrid?.length || !target) return;
      const unitId = this.currentUnitId;
      const fWorksheet = this.univerAPI.getActiveWorkbook()?.getActiveSheet();
      if (!fWorksheet) return;
      const sRows = sourceGrid.length;
      const sCols = sourceGrid[0].length;

      try {
        for (let r = target.startRow; r <= target.endRow; r++) {
          for (let c = target.startColumn; c <= target.endColumn; c++) {
            if (this.currentUnitId !== unitId) return;
            const component = sourceGrid[(r - target.startRow) % sRows][(c - target.startColumn) % sCols];
            const cell = fWorksheet.getRange(r, c);
            if (component) {
              applyComponent(this.univerAPI, cell, component, true);
            } else {
              cell.setCustomMetaData(null);
            }
          }
        }

        if (clearSource && this.currentUnitId === unitId) {
          for (let r = clearSource.startRow; r <= clearSource.endRow; r++) {
            for (let c = clearSource.startColumn; c <= clearSource.endColumn; c++) {
              fWorksheet.getRange(r, c).setCustomMetaData(null);
            }
          }
        }

        this._enforceRowRules(fWorksheet, target);
      } catch (err) {
        console.warn('[_replayMeta] aborted', err);
      }
    },

    async toggleProtection() {
      this.isProtected = !this.isProtected;
      this._previewLocked$?.next(this.isProtected);
      if (this.isProtected) {
        await this._installProtection(this.currentUnitId, this.computeLockedRangesFromWorkbook());
      } else {
        this._removeProtection();
      }
    },

    autoFormatDate({ worksheet, row, column }) {
      const fRange = worksheet.getRange(row, column);
      const value = fRange.getValue();
      if (value && typeof value === 'string' && !isNaN(new Date(value).getTime())) {
        fRange.setNumberFormat('dd/mm/yyyy');
      }
    },

    // ----- xlsx import / export -----
    async importFile(e) {
      if (!(await this.confirmOverwrite())) return;
      const file = e.files[0];
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(file);

      this.sheetType = file.name.startsWith('_CWK') ? 'cwk' : 'rpk';
      setEditorMode(this.sheetType);

      await this.$nextTick();

      if (!this.univer) await this.createMainUniver();
      this.disposeNavInstance();

      const { commentsMap, locksMap } = this.extractExcelMetadata(workbook);

      LuckyExcel.transformExcelToUniver(
        file,
        (univerData) => this.applyExcelImport(file.name, univerData, commentsMap, locksMap),
        (error) => this.$toast.add({ severity: 'error', summary: 'Error', detail: error, life: 3000 }),
      );

      if (this.sheetType === 'cwk') {
        await this.createNavUniver();
        this.setupRPKValueNavigation();
        this.setupAutoSheetName();
        this.loadLatestRPKIntoNav();
      }
    },
    extractExcelMetadata(workbook) {
      const commentsMap = {};
      const locksMap = {};
      workbook.eachSheet((worksheet, index) => {
        const sheetComments = commentsMap[index] = {};
        const sheetLocks = locksMap[index] = {};
        worksheet.eachRow((row) => {
          row.eachCell((cell) => {
            // Normalise =[1]Sales!B6 → =Sales!B6.
            if (cell.value && typeof cell.value.formula === 'string') {
              const newFormula = cell.value.formula.replace(/\[\d+\]/g, '');
              cell.value = { ...cell.value, formula: newFormula };
            }

            if (cell.note) {
              // Rich-text notes have .texts; plain notes are strings.
              let noteText = '';
              if (typeof cell.note === 'string') {
                noteText = cell.note;
              } else if (Array.isArray(cell.note.texts)) {
                noteText = cell.note.texts.map((t) => t.text).join('');
              }
              // xlsx notes are commonly "author: text" — strip author.
              const parts = noteText.split(':');
              sheetComments[cell.address] = (parts.length > 1 ? parts[1] : parts[0]).trim();
            }

            // Locked by default; protection.locked=false unlocks.
            const locked = cell.style.protection?.locked ?? true;
            sheetLocks[cell.address] = locked;
          });
        });
      });
      return { commentsMap, locksMap };
    },
    async applyExcelImport(fileName, univerData, commentsMap, locksMap) {
      this.fileName = fileName;
      // Unique id so re-importing the same file doesn't collide.
      univerData.id = `xlsx-import-${Date.now()}`;
      await this._swapMainUnit(univerData);
      this.univerData = univerData;

      const fWorkbook = this.univerAPI.getActiveWorkbook();
      const fWorksheets = fWorkbook.getSheets();

      // LuckyExcel names sheets 'sheet-1', 'sheet-2', … (1-based).
      for (const [sheetId, sheet] of Object.entries(univerData.sheets)) {
        const sheetIndex = parseInt(sheetId.split('-')[1], 10) - 1;
        const fWorksheet = fWorksheets[sheetIndex];
        const sheetComments = commentsMap[sheetIndex + 1] || {};
        const sheetLocks = locksMap[sheetIndex + 1] || {};

        Object.entries(sheet.cellData || {}).forEach(([rowIndex, rowData]) => {
          Object.entries(rowData).forEach(([colIndex]) => {
            const cellAddr = this.getHeaderLetter(parseInt(colIndex, 10)) + (parseInt(rowIndex, 10) + 1);
            const fRange = fWorksheet.getRange(cellAddr);

            if (sheetComments[cellAddr]) {
              const richText = this.univerAPI.newRichText().insertText(sheetComments[cellAddr]);
              const commentBuilder = this.univerAPI.newTheadComment().setContent(richText);
              fRange.addCommentAsync(commentBuilder);
            } else if (sheetLocks[cellAddr]) {
              fRange.setBackgroundColor(LOCKED_CELL_BG);
            }
          });
        });

        fWorksheet.setDefaultStyle({ ...IMPORT_DEFAULT_STYLE });
      }
    },
    // New sheet (+): continue the numbering already on the tabs (Pg_120 -> Pg_121), and on CWK
    // seed the page with its header band and sample row.
    setupAutoSheetName() {
      if (this.autoSheetNameDisposable) {
        this.autoSheetNameDisposable.dispose();
        this.autoSheetNameDisposable = null;
      }
      this.autoSheetNameDisposable = this.univerAPI.addEvent(
        this.univerAPI.Event.SheetCreated,
        ({ workbook: fWorkbook, worksheet: fWorksheet }) => {
          const newSheetName = this._nextSheetName(fWorkbook, fWorksheet);
          fWorksheet.setName(newSheetName);
          this._refreshSheetStatus();
          if (this.sheetType !== 'cwk') return;
          this._seedCwkPage(fWorksheet, newSheetName.replace('_', ' '));
        },
      );
    },
    // Rows 1-3 are the CWK header band; row 6 is the first data row on every page in templates/cwk.
    // Its two roll-ups are seeded as grey italic LABEL text - as live formulas the sample would
    // render its result (0) rather than the shape it exists to demonstrate.
    _seedCwkPage(fWorksheet, displayName) {
      // 0-based; the formulas below name row 6 in A1 terms, so the two have to move together.
      const SAMPLE_ROW = 5;
      const cells = [
        [0, 0, 'LABEL',   'Entity ID',    'center'],
        [0, 6, 'HEADER',  'RPK',          null],
        [1, 0, 'LABEL',   'Currency',     'center'],
        [1, 1, 'LABEL',   '_cwk',         'center'],
        [1, 2, 'HEADER',  'Conso Total',  'center'],
        [1, 3, 'HEADER',  'DR',           'center'],
        [1, 4, 'HEADER',  'CR',           'center'],
        [1, 5, 'HEADER',  'Grp Total',    'center'],
        [2, 0, 'LABEL',   displayName,    'center'],
        [SAMPLE_ROW, 0, 'LABEL',  'Sample row - replace with account name', null],
        [SAMPLE_ROW, 2, 'LABEL',  '=F6+D6-E6',    null],
        [SAMPLE_ROW, 5, 'LABEL',  '=SUM(G6:Z6)',  null],
      ];
      for (const [r, c, comp, val, align] of cells) {
        const cell = fWorksheet.getRange(r, c);
        // bypassPlacement: G1 is HEADER even though col G is VALUE-only in CWK.
        applyComponent(this.univerAPI, cell, comp, true, true);
        // A bare '=...' string becomes a formula on write, so pass cell data instead. FORCE_STRING
        // is what survives an edit: the raw view shows '=..., which commits back as text not formula.
        const isFormulaText = typeof val === 'string' && val.startsWith('=');
        cell.setValue(isFormulaText ? { v: val, t: CellValueType.FORCE_STRING } : val);
        if (align) cell.setHorizontalAlignment(align);
        if (r === SAMPLE_ROW) {
          cell.setFontStyle('italic');
          cell.setFontColor(SAMPLE_FONT_COLOR);
        }
      }
    },
    // The prefix comes from the sheet type, never from the neighbouring tabs. CWK pages really are
    // one Pg_ series, but RPK codes (BS, frs7a, ICO_P2) are unrelated names - continuing whichever
    // one happened to end in a digit named the first new RPK sheet ICO_P3.
    _nextSheetName(fWorkbook, fWorksheet) {
      const others = fWorkbook.getSheets().filter((s) => s.getSheetId() !== fWorksheet.getSheetId());
      // Codes taken elsewhere count too, or + hands out a name the rename check would refuse.
      const taken = new Set([...others.map((s) => s.getSheetName()), ...this._takenCodes]);
      const prefix = this.sheetType === 'cwk' ? 'Pg_' : 'Sheet_';
      // A regex literal, not new RegExp with a template string: `\d` in a template literal
      // collapses to `d`, which silently matched nothing and restarted the numbering at 1.
      let next = [...taken]
        .filter((name) => name.startsWith(prefix) && /^\d+$/.test(name.slice(prefix.length)))
        .reduce((max, name) => Math.max(max, parseInt(name.slice(prefix.length), 10)), 0) + 1;
      // A name over the code limit is rejected on save, so trim the prefix rather than the number.
      const fit = (n) => prefix.slice(0, Math.max(0, SHEET_NAME_MAX_LENGTH - String(n).length)) + n;
      while (taken.has(fit(next))) next += 1;
      return fit(next);
    },
    async loadLatestRPKIntoNav() {
      try {
        const response = await sheetApi.sheetListActiveTemplates('rpk');
        if (!response.success || !response.data.listSet.length) return;
        this.loadSheetsIntoNav(response.data.listSet.slice(0, 5));
      } catch (err) {
        this.toastApiError(err, 'Failed to load RPK navigation templates');
      }
    },
    // Main: selecting a formula scrolls nav to its target. VALUE is armed from the components
    // menu or the Set Options popup ("Pick from RPK pane"); a nav click then proposes =Sheet!A1.
    setupRPKValueNavigation() {
      ['navNavigationDisposable', 'navLinkDisposable'].forEach((key) => {
        this[key]?.dispose();
        this[key] = null;
      });

      this.navNavigationDisposable = this.univerAPI.addEvent(this.univerAPI.Event.SelectionMoveEnd, ({ worksheet, selections }) => {
        if (!selections.length) return;
        const selection = selections[0];
        const fRange = worksheet.getRange(selection.startRow, selection.startColumn);

        // Clicking away from any armed VALUE cell cancels the arm and any unconfirmed link.
        const armed = this.armedValueCell;
        if (armed && !armed.cells.some((c) => c.row === selection.startRow && c.col === selection.startColumn)) {
          this._clearValueArm();
        }

        // Jump nav pane to the formula's target (if any).
        const formula = fRange.getFormula();
        if (!formula) return;
        const match = formula.match(/^=-?'?([^']*)'?!([A-Z]+[0-9]+)$/);
        if (!match) return;
        const [, sheetName, cellAddress] = match;
        const fWorkbookNav = this.univerAPINav?.getActiveWorkbook();
        if (!fWorkbookNav) return;
        const fWorksheetNav = fWorkbookNav.getSheetByName(sheetName);
        if (!fWorksheetNav) return;
        fWorkbookNav.setActiveSheet(fWorksheetNav);
        const fRangeNav = fWorksheetNav.getRange(cellAddress);
        fWorksheetNav.scrollToCell(fRangeNav.getRow(), fRangeNav.getColumn());
        fWorkbookNav.setActiveRange(fRangeNav);
      });

      this.navLinkDisposable = this.univerAPINav.addEvent(this.univerAPINav.Event.SelectionMoveEnd, ({ worksheet: navWorksheet, selections }) => {
        const armed = this.armedValueCell;
        if (!armed || !selections.length) return;
        const selection = selections[0];
        const sheetName = navWorksheet.getSheetName();
        const cellAddress = navWorksheet.getRange(selection.startRow, selection.startColumn).getA1Notation();
        const safeName = /^[A-Za-z_][A-Za-z0-9_]*$/.test(sheetName) ? sheetName : `'${sheetName}'`;
        const first = armed.cells[0];
        const more = armed.cells.length > 1 ? ` +${armed.cells.length - 1}` : '';
        this.pendingValueLink = {
          formula: `=${safeName}!${cellAddress}`,
          sourceLabel: `${armed.worksheet.getSheetName()}!`
            + `${armed.worksheet.getRange(first.row, first.col).getA1Notation()}${more}`,
          targetLabel: `${sheetName}!${cellAddress}`,
        };
      });
    },
    // Arm CWK cells so the next RPK-pane click proposes a link for them. Each cell keeps its
    // pre-arm content: the components menu wipes the cell to '=', and Cancel has to restore it.
    _armValueCells(fWorksheet, cells) {
      if (!fWorksheet || !cells?.length) return;
      const armed = cells.map(({ row, col, previous }) => ({
        row,
        col,
        previous: previous !== undefined ? previous : cellContent(fWorksheet, row, col),
      }));
      this.armedValueCell = { worksheet: fWorksheet, cells: armed };
      this.pendingValueLink = null;
      this.$toast.add({
        severity: 'info', summary: 'Select a RPK cell',
        detail: 'Click the cell in the RPK pane to link, then confirm.', life: 6000,
      });
    },
    _clearValueArm() {
      this.armedValueCell = null;
      this.pendingValueLink = null;
    },
    // The RPK-pane click only proposes the target; the cell is written on confirm, so a
    // mis-click can be re-picked instead of overwriting the link that was already there.
    confirmValueLink() {
      const pending = this.pendingValueLink;
      const armed = this.armedValueCell;
      if (!pending || !armed) return;
      armed.cells.forEach(({ row, col }) => armed.worksheet.getRange(row, col).setValue(pending.formula));
      this._clearValueArm();
      this.$toast.add({
        severity: 'success', summary: 'Linked',
        detail: pending.formula.slice(1), life: 2000,
      });
    },
    // Cancel puts each armed cell back to what it held before the arm — an existing link the
    // menu wiped, or blank. Clicking away instead leaves the cell as armed, so a VALUE cell can
    // still be emptied by inserting the component and not linking.
    cancelValueLink() {
      const armed = this.armedValueCell;
      if (armed) {
        armed.cells.forEach(({ row, col, previous }) => {
          armed.worksheet.getRange(row, col).setValue(previous == null ? '' : previous);
        });
      }
      this._clearValueArm();
    },
    async exportFile() {
      try {
        const fWorkbook = this.univerAPI?.getActiveWorkbook();
        if (!fWorkbook) {
          this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No active workbook found', life: 3000 });
          return;
        }

        const snapshot = fWorkbook.save();
        const fileName = this.fileName || 'spreadsheet.xlsx';

        const workbookBuffer = await new Promise((resolve, reject) => {
          LuckyExcel.transformUniverToExcel({
            snapshot,
            getBuffer: true,
            success: (buffer) => resolve(buffer),
            error: (err) => reject(err),
          });
        });

        const exceljsWorkbook = new ExcelJS.Workbook();
        await exceljsWorkbook.xlsx.load(workbookBuffer);

        const tasks = fWorkbook.getSheets().map(async (fWorksheet, index) => {
          const exceljsWorksheet = exceljsWorkbook.getWorksheet(index + 1);
          if (!exceljsWorksheet) return;

          fWorksheet.getComments().forEach((fComment) => {
            const r = fComment.getRange();
            const cell = exceljsWorksheet.getCell(r.getRow() + 1, r.getColumn() + 1);
            cell.note = fComment.getRichText().toPlainText();
          });

          // Unlock everything, re-lock the grey cells.
          exceljsWorksheet.eachRow({ includeEmpty: true }, (row) => {
            row.eachCell({ includeEmpty: true }, (cell) => {
              cell.style = { ...cell.style, protection: { locked: false } };
            });
          });

          const univerCellData = this.univerData?.sheets[fWorksheet.getSheetId()]?.cellData || {};
          Object.entries(univerCellData).forEach(([r, row]) => {
            Object.entries(row).forEach(([c]) => {
              const fRange = fWorksheet.getRange(parseInt(r, 10), parseInt(c, 10));
              if (fRange.getBackground() !== LOCKED_CELL_BG) return;
              const excelCell = exceljsWorksheet.getRow(parseInt(r, 10) + 1).getCell(parseInt(c, 10) + 1);
              excelCell.style = { ...excelCell.style, protection: { locked: true } };
              excelCell.fill = undefined;
            });
          });

          await exceljsWorksheet.protect(null, { selectLockedCells: true, selectUnlockedCels: true });
        });
        await Promise.all(tasks);

        // Empty strings → null so Excel renders blank cells.
        exceljsWorkbook.eachSheet((ws) => {
          ws.eachRow((row) => {
            row.eachCell((cell) => { if (cell.value === '') cell.value = null; });
          });
        });

        const buffer = await exceljsWorkbook.xlsx.writeBuffer();
        this.downloadBuffer(buffer, fileName);
      } catch (error) {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: error, life: 3000 });
      }
    },
    downloadBuffer(buffer, fileName) {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // ----- DB-backed template set loading -----
    // One shape for both callers: the load response carries elimination settings, the save response
    // does not, so a save keeps whatever the load established.
    _toSheetIdentity(row, univerSheetId) {
      return {
        sheetId: row.sheetId,
        templateId: row.templateId,
        isClosed: row.isClosed,
        code: row.sheetCode,
        eliminationSettings: row.eliminationSettings
          || this.sheetIdentity[univerSheetId]?.eliminationSettings
          || [],
      };
    },
    // The snapshot builder names sheets by position, so pair the loaded rows with the worksheets
    // the workbook ended up with rather than reconstructing that naming here.
    _captureSheetIdentity(sheets) {
      const worksheets = this.univerAPI.getActiveWorkbook()?.getSheets() || [];
      this.sheetIdentity = Object.fromEntries(
        worksheets
          .map((fWorksheet, index) => [fWorksheet.getSheetId(), sheets[index]])
          .filter(([, sheet]) => sheet)
          .map(([univerSheetId, sheet]) => [univerSheetId, this._toSheetIdentity(sheet, univerSheetId)]),
      );
    },
    // Sheets created by this save have DB ids now; without re-keying, a rename in the next save
    // would insert them a second time. Tabs missing from the payload are the ones this save
    // deleted, so dropping them stops the next save deleting them again.
    _applySavedIdentity(sheetsJson, saved) {
      this.sheetIdentity = Object.fromEntries(
        sheetsJson
          .map((s, index) => [s.univerSheetId, saved[index]])
          .filter(([, row]) => row)
          .map(([univerSheetId, row]) => [univerSheetId, this._toSheetIdentity(row, univerSheetId)]),
      );
      // Before the baseline, or the undo entries this pushes leave it describing nothing.
      this._refreshSheetStatus();
    },
    // Tab colours and the header count, in one pass so they cannot disagree. A tab with no
    // identity has not been saved yet, so it is not live either.
    _refreshSheetStatus() {
      const fWorkbook = this.univerAPI.getActiveWorkbook();
      if (!fWorkbook) return;
      const worksheets = fWorkbook.getSheets();
      let notLive = 0;
      for (const fWorksheet of worksheets) {
        const identity = this.sheetIdentity[fWorksheet.getSheetId()];
        const isNotLive = !identity || identity.isClosed;
        if (isNotLive) notLive += 1;
        const wanted = isNotLive ? INACTIVE_TAB_COLOR : '';
        // Re-setting the same colour still pushes an undo entry, so only write on a change.
        if ((fWorksheet.getTabColor() || '') !== wanted) fWorksheet.setTabColor(wanted);
      }
      // Counted off the workbook, not the load response, or a sheet added since the load is
      // missing from both numbers until the next reload.
      if (this.loadedType) {
        this.loadedLabel = `${this.loadedType} v${this.loadedVersion} (Draft) — ${worksheets.length} sheets`
          + (notLive ? ` — ${notLive} not published yet` : '');
      }
    },
    // Both the sheet-bar entry and the command it dispatches route here, so Preview mode refuses
    // before the confirm dialog rather than after it.
    _guardRemoveSheet(e) {
      if (this.isProtected) {
        e.cancel = true;
        this.$toast.add({
          severity: 'error', summary: 'Error',
          detail: 'Cannot delete sheets in Preview mode', life: 3000,
        });
        return;
      }
      if ((this.univerAPI.getActiveWorkbook()?.getSheets() || []).length <= 1) {
        e.cancel = true;
        this.$toast.add({
          severity: 'error', summary: 'Error',
          detail: 'A template set must keep at least one sheet', life: 3000,
        });
      }
    },
    // Sheet codes are unique across both types, so a CWK tab can collide with an RPK sheet that
    // is nowhere in this workbook. Cache the codes in use elsewhere so the rename interceptor -
    // which has to answer synchronously - can refuse one. Closed sheets are not listed, so the
    // save-time check in do_template_set_save stays the authority.
    // Cancelling the command alone reverts the name AND drops the tab out of edit mode, so a
    // rejected rename looked like nothing happened. Dismissing the dialog puts the tab back into
    // rename, the way Univer's own duplicate-name dialog does.
    _rejectRename(e, detail) {
      e.cancel = true;
      const subUnitId = e.params?.subUnitId
        ?? this.univerAPI.getActiveWorkbook()?.getActiveSheet()?.getSheetId();
      const dialogService = this.univer.__getInjector().get(IDialogService);
      const dismiss = () => {
        dialogService.close(SHEET_NAME_ERROR_DIALOG);
        if (subUnitId) this.univerAPI.executeCommand(RENAME_SHEET_OPERATION, { subUnitId });
      };
      // showOk / showCancel draw the footer; cancelText / confirmText belong to IConfirmService
      // and render nothing here. Both buttons reopen the rename, matching Univer's own name alerts.
      dialogService.open({
        id: SHEET_NAME_ERROR_DIALOG,
        title: { title: SHEET_NAME_ERROR_TITLE },
        children: { title: detail },
        showOk: true,
        showCancel: true,
        onOk: dismiss,
        onCancel: dismiss,
        onClose: dismiss,
      });
    },
    async _loadTakenCodes(sheets) {
      this._takenCodes = new Set();
      try {
        const response = await sheetApi.sheetList({ code: null }, null);
        if (!response.success) return;
        const ownIds = new Set(sheets.map((sheet) => sheet.sheetId));
        this._takenCodes = new Set(
          response.data.listSet
            .filter((sheet) => !ownIds.has(sheet.identifier))
            .map((sheet) => sheet.code),
        );
      } catch (err) {
        this.toastApiError(err, 'Failed to load sheet codes in use');
      }
    },
    // What still points at the sheets this save would remove. Formula references are read off the
    // extracted payload - the same shape the backend scans with re.findall(r'(\w+)!\w+') - so the
    // two agree on what counts as a reference.
    _deletedSheetDependencies(sheetsJson) {
      const present = new Set(sheetsJson.map((s) => s.univerSheetId));
      const going = Object.entries(this.sheetIdentity)
        .filter(([univerSheetId]) => !present.has(univerSheetId))
        .map(([, identity]) => identity)
        .filter((identity) => identity.code);
      if (!going.length) return [];
      const remaining = sheetsJson.map((s) => ({ name: s.sheetName, text: JSON.stringify(s.content) }));
      // Plain indexOf rather than a RegExp built from the code: escaping a dynamic pattern is easy
      // to get subtly wrong, and a wrong pattern here fails silently. The preceding character must
      // not be a word character, or Pg_1 would match inside XPg_1!.
      const references = (text, code) => {
        const needle = `${code}!`;
        for (let at = text.indexOf(needle); at !== -1; at = text.indexOf(needle, at + 1)) {
          if (at === 0 || !/[A-Za-z0-9_]/.test(text[at - 1])) return true;
        }
        return false;
      };
      return going.map((identity) => ({
        code: identity.code,
        referencedBy: remaining
          .filter((sheet) => references(sheet.text, identity.code))
          .map((sheet) => sheet.name),
        eliminationSettings: identity.eliminationSettings || [],
      })).filter((dep) => dep.referencedBy.length || dep.eliminationSettings.length);
    },
    _deletedSheetIds(sheetsJson) {
      const present = new Set(sheetsJson.map((s) => s.univerSheetId));
      return Object.entries(this.sheetIdentity)
        .filter(([univerSheetId]) => !present.has(univerSheetId))
        .map(([, identity]) => identity.sheetId)
        .filter((sheetId) => sheetId != null);
    },
    // Deleting a referenced sheet is allowed - removing a group of sheets together is a real
    // case - but a formula pointing at a sheet that is gone silently evaluates to 0, so say so.
    _deletedSheetWarning(dependencies) {
      if (!dependencies.length) return '';
      const lines = dependencies.map((dep) => {
        const parts = [];
        if (dep.referencedBy.length) parts.push(`referenced by ${dep.referencedBy.join(', ')}`);
        if (dep.eliminationSettings.length) {
          parts.push(`used by elimination setting ${dep.eliminationSettings.join(', ')}`);
        }
        return `${dep.code} is ${parts.join(' and ')}.`;
      });
      // The dialog renders one run of text, so these read as sentences rather than a list.
      return ` ${lines.join(' ')} Formulas pointing at a removed sheet evaluate to 0.`;
    },
    confirmDeletedSheets(count, dependencies) {
      return new Promise((resolve) => {
        const noun = count === 1 ? 'sheet' : 'sheets';
        this.$confirm.require({
          header: 'Confirmation',
          message: `${count} ${noun} will be removed from this template set.`
            + this._deletedSheetWarning(dependencies)
            + ` This version's content for ${count === 1 ? 'that sheet' : 'those sheets'} is deleted`
            + ' permanently and cannot be undone - export the template set first if you may need it'
            + ' back. Are you sure you want to continue?',
          icon: 'pi pi-exclamation-triangle',
          accept: () => resolve(true),
          reject: () => resolve(false),
          onHide: () => resolve(false),
        });
      });
    },
    async saveSet() {
      if (!this.loadedType || !this.loadedVersion) return;
      const sheetsJson = extractWorkbookToDbJson(this.univerAPI);
      const deletedSheetIds = this._deletedSheetIds(sheetsJson);
      if (deletedSheetIds.length) {
        const dependencies = this._deletedSheetDependencies(sheetsJson);
        if (!(await this.confirmDeletedSheets(deletedSheetIds.length, dependencies))) return;
      }
      this.isLoading = true;
      try {
        const savedSeq = this._dirtySeq;
        const payload = sheetsJson.map((s) => ({
          sheetId: this.sheetIdentity[s.univerSheetId]?.sheetId ?? null,
          sheetCode: s.sheetName,
          content: s.content,
        }));
        const res = await templateApi.templateSetSave(
          this.loadedType, this.loadedVersion, payload, deletedSheetIds,
        );
        this._applySavedIdentity(sheetsJson, res.data?.saved || []);
        // Edits made after the payload was extracted did not reach the server, so they stay unsaved -
        // and then no stack depth describes the saved content any more.
        if (this._dirtySeq === savedSeq) {
          this.isDirty = false;
          this._undoBaseline = this._undoDepth;
        } else {
          this._undoBaselineValid = false;
        }
        const { updated, created, renamed, deleted } = res.data || {};
        const counts = [`${updated} updated`, `${created} created`];
        if (renamed) counts.push(`${renamed} renamed`);
        if (deleted) counts.push(`${deleted} deleted`);
        this.$toast.add({
          severity: 'success',
          summary: 'Saved',
          detail: counts.join(', '),
          life: 3000,
        });
      } catch (e) {
        // Sheet name clashes and length limits come back as a message worth reading.
        this.toastApiError(e, 'Failed to save');
      } finally {
        this.isLoading = false;
      }
    },
    confirmOverwrite() {
      return new Promise((resolve) => {
        if (!this.currentUnitId || !this.isDirty) { resolve(true); return; }
        this.$confirm.require({
          header: 'Confirmation',
          message: 'Loading will replace the sheets currently open and any unsaved changes will be lost. Are you sure you want to continue?',
          icon: 'pi pi-exclamation-triangle',
          accept: () => resolve(true),
          reject: () => resolve(false),
          // Escape and the header X go through neither accept nor reject; without this the
          // promise never settles and the caller waits forever.
          onHide: () => resolve(false),
        });
      });
    },
    async openPicker(type) {
      if (!(await this.confirmOverwrite())) return;
      this.pickerType = type;
      this.pickerVisible = true;
    },
    // Publishing a draft retires every active sheet it has no template for (do_template_publish),
    // and the editor is the only place a draft can be added to an existing version. So before the
    // set is loaded, offer to seed one for each active sheet the version is missing. Declining
    // loads the version as it is; the check failing is not a reason to refuse the load.
    async offerMissingDrafts(type, version) {
      let missing = [];
      try {
        const res = await templateApi.templateSetMissingDrafts(type, version);
        missing = res.data?.sheets || [];
      } catch (e) {
        this.toastApiError(e, 'Failed to check for sheets without a draft template');
        return;
      }
      if (!missing.length) return;

      const codes = missing.map((sheet) => sheet.sheetCode).join(', ');
      const proceed = await new Promise((resolve) => {
        this.$confirm.require({
          header: 'Confirmation',
          message: `${missing.length} active ${type} sheet(s) have no draft template in version ${version}: `
            + `${codes}. Publishing this version would retire them. `
            + 'Create draft template(s) for these sheet(s) now?',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Create',
          rejectLabel: 'Skip',
          accept: () => resolve(true),
          reject: () => resolve(false),
          onHide: () => resolve(false),
        });
      });
      if (!proceed) return;

      try {
        const res = await templateApi.templateSetCreateDrafts(type, version, missing.map((sheet) => sheet.sheetId));
        this.$toast.add({
          severity: 'success', summary: 'Success',
          detail: res.message || 'Draft templates created successfully.', life: 6000,
        });
      } catch (e) {
        this.toastApiError(e, 'Failed to create draft templates');
      }
    },
    async onPickerLoad({ type, version }) {
      const isCwk = type === 'CWK';
      await this.offerMissingDrafts(type, version);
      this.isLoading = true;
      this.loadedLabel = '';
      this.sheetIdentity = {};
      this._takenCodes = new Set();
      // Disposed here rather than after the load: the picker's modal mask has been over the nav
      // grid for far longer than its 100ms hover debounce, so nothing is left pending to flush
      // into the disposed unit. Done later, the closing mask lets the pointer land on the grid.
      if (!isCwk) this.disposeNavInstance();
      try {
        const res = await templateApi.templateSetLoad(type, version);
        const sheets = res.data?.sheets || [];
        if (!sheets.length) {
          this.$toast.add({ severity: 'warn', summary: 'Warning', detail: 'No sheets in this version', life: 3000 });
          return;
        }

        this.sheetType = type.toLowerCase();
        setEditorMode(this.sheetType);
        this.loadedType = type;
        this.loadedVersion = version;
        this.fileName = '';
        await this.$nextTick();

        // Sheets not yet published sit to the right of the live ones. The backend already ordered
        // by sequence and sort is stable, so a partition on isClosed keeps sequence order within
        // each group. Tab order is presentational - save_set never rewrites Sheet.sequence from it,
        // which is what calculate actually walks.
        const ordered = [...sheets].sort((a, b) => Number(!!a.isClosed) - Number(!!b.isClosed));
        const dbSheets = ordered.map((sheet) => {
          const parsed = typeof sheet.content === 'string' ? JSON.parse(sheet.content) : sheet.content;
          return {
            name: sheet.sheetCode || sheet.sheetName || `Sheet ${sheet.sequence}`,
            rows: parsed?.template || [],
          };
        });
        const unitId = `template-set-${type}-${version}-draft-${Date.now()}`;
        const snapshot = buildWorkbookSnapshot(unitId, dbSheets);
        await this._swapMainUnit(snapshot);

        applyBulkRules(this.univerAPI, this.univerAPI.getActiveWorkbook(), collectRuleCells(dbSheets));
        await this.applyEditLock(unitId, computeLockedRangesBySheet(dbSheets));
        this._captureSheetIdentity(ordered);
        this._refreshSheetStatus();
        this._loadTakenCodes(ordered);
        this.setupAutoSheetName();
        this._installPreviewMenuLocks();
        this._installFormulaSuggestionFilter();
        // Everything above is the load writing itself in; everything below leaves the set open and
        // typeable while it fetches, so this is the last point a clean baseline is still true.
        this.isDirty = false;
        this._undoBaseline = this._undoDepth;
        this._undoBaselineValid = true;

        // RPK tore the nav down before the load; only CWK has anything left to do here.
        if (isCwk) {
          await this.loadNavRPK();
          this.setupRPKValueNavigation();
          this.createMenus(); // register when moving from RPK to CWK for CWK-specific items (idempotent via _contextMenusInstalled)
        }

        this._refreshSheetStatus();
      } catch (e) {
        this.toastApiError(e, 'Failed to load template set');
      } finally {
        this.isLoading = false;
      }
    },
    async loadSheetsIntoNav(sheets) {
      const dbSheets = sheets.map((sheet) => ({
        name: sheet.code || `Sheet ${sheet.sequence || ''}`,
        rows: sheet?.template?.content || [],
      }));
      const unitId = `nav-${Date.now()}`;
      await this._swapNavUnit(buildWorkbookSnapshot(unitId, dbSheets));
      applyBulkRules(this.univerAPINav, this.univerAPINav.getActiveWorkbook(), collectRuleCells(dbSheets));
      return unitId;
    },

    async loadNavRPK() {
      if (!this.univerNav) await this.createNavUniver();
      const response = await sheetApi.sheetListActiveTemplates('rpk');
      if (!response.success || !response.data.listSet.length) return;
      const unitId = await this.loadSheetsIntoNav(response.data.listSet);
      const fWorkbookNav = this.univerAPINav.getActiveWorkbook();
      const perm = fWorkbookNav?.getPermission();
      const def = perm?.permissionPointsDefinition;
      if (perm && def?.WorkbookEditablePermission) {
        perm.setWorkbookPermissionPoint(unitId, def.WorkbookEditablePermission, false);
      }
    },

    applyDateTimePick() {
      const ranges = this.dateTimePickerRanges || [];
      const d = this.dateTimePickerValue;
      if (!ranges.length || !d) return;
      const fWorksheet = this.univerAPI.getActiveWorkbook()?.getActiveSheet();
      if (!fWorksheet) return;
      const formatted = formatDateDDMMYYYY(d);
      for (const r of ranges) {
        fWorksheet.getRange(r.startRow, r.startColumn).setValue(formatted);
      }
      this.isDateTimePickerVisible = false;
      this.dateTimePickerRanges = null;
    },

    applyNumberPick() {
      const ranges = this.numberPickerRanges || [];
      const fWorksheet = this.univerAPI.getActiveWorkbook()?.getActiveSheet();
      if (fWorksheet && ranges.length) {
        const raw = this.numberPickerValue;
        const num = (raw == null || Number.isNaN(Number(raw))) ? 0 : Number(raw);
        const value = this.numberPickerMode === 'specific' ? num : NUMBER_NO_DEFAULT;
        const pattern = buildNumberPattern(this.numberPickerPercentage, this.numberPickerDecimals);
        for (const r of ranges) {
          const cell = fWorksheet.getRange(r.startRow, r.startColumn);
          cell.setValue(value);
          // Null pattern falls back to 'General' so any prior format is cleared.
          safeSetNumberFormat(cell, pattern || 'General');
        }
      }
      this._resetNumberPicker();
    },
    cancelNumberPick() {
      // Selecting the option already wrote a placeholder ('No Default' / 0) into the cell,
      // so just drop picker state and leave that placeholder in place.
      this._resetNumberPicker();
    },
    _resetNumberPicker() {
      this.isNumberPickerVisible = false;
      this.numberPickerRanges = null;
      this.numberPickerValue = null;
      this.numberPickerMode = 'specific';
      this.numberPickerPercentage = false;
      this.numberPickerDecimals = 'auto';
    },

    // Open the Set Options popup for a FORMULA / VALUE cell (excel formula + percentage /
    // decimal formatting), pre-filled from the cell's current formula and number format.
    _openFormulaOptions(fWorksheet, row, column, component) {
      if (!fWorksheet) return;
      const cell = fWorksheet.getRange(row, column);
      const formula = cell.getFormula() || '';
      const raw = formula || cell.getValue();
      const rawStr = raw == null ? '' : String(raw);
      this.formulaPickerValue = rawStr.startsWith('=') ? rawStr : (rawStr ? `=${rawStr}` : '=');

      const pattern = cell.getCellStyleData()?.n?.pattern || '';
      this.formulaPickerPercentage = pattern.includes('%');
      this.formulaPickerDecimals = pattern ? decimalPlacesOfPattern(pattern) : 'auto';

      this.formulaPickerComponent = component;
      this.formulaPickerCanLink = component === 'VALUE' && !!this.univerAPINav;
      this.formulaPickerRanges = [{ startRow: row, endRow: row, startColumn: column, endColumn: column }];
      this.isFormulaPickerVisible = true;
    },
    applyFormulaPick() {
      const ranges = this.formulaPickerRanges || [];
      const fWorksheet = this.univerAPI.getActiveWorkbook()?.getActiveSheet();
      if (fWorksheet && ranges.length) {
        let formula = String(this.formulaPickerValue ?? '').trim();
        if (!formula.startsWith('=')) formula = `=${formula}`;
        const pattern = buildNumberPattern(this.formulaPickerPercentage, this.formulaPickerDecimals);
        for (const r of ranges) {
          const cell = fWorksheet.getRange(r.startRow, r.startColumn);
          cell.setValue(formula);
          // Null pattern falls back to 'General' so any prior format is cleared.
          safeSetNumberFormat(cell, pattern || 'General');
        }
      }
      this._resetFormulaPicker();
    },
    // VALUE (RPK link): arm a single cell without opening the Set Options dialog.
    _armValueCell(fWorksheet, row, column) {
      this._armValueCells(fWorksheet, [{ row, col: column }]);
    },
    // VALUE only: arm the popup's cells, then let the RPK pane propose the target.
    armFormulaFromPopup() {
      const ranges = this.formulaPickerRanges || [];
      const fWorksheet = this.univerAPI.getActiveWorkbook()?.getActiveSheet();
      if (ranges.length) {
        this._armValueCells(fWorksheet, ranges.map((r) => ({ row: r.startRow, col: r.startColumn })));
      }
      this._resetFormulaPicker();
    },
    cancelFormulaPick() {
      this._resetFormulaPicker();
    },
    _resetFormulaPicker() {
      this.isFormulaPickerVisible = false;
      this.formulaPickerRanges = null;
      this.formulaPickerComponent = null;
      this.formulaPickerValue = '=';
      this.formulaPickerPercentage = false;
      this.formulaPickerDecimals = 'auto';
      this.formulaPickerCanLink = false;
    },

    applyAccountInsert() {
      const account = this.accountCode.trim();
      const translation = this.translationCode !== 'None' ? this.translationCode : '';
      const fWorksheet = this.univerAPI?.getActiveWorkbook()?.getActiveSheet();
      if (fWorksheet && this.accountTargetRow !== null) {
        // Prefix runs first: it creates the comma the suffix pass splices against.
        if (account) this._spliceSecondColumn(fWorksheet, this.accountTargetRow, account, 'prefix');
        if (translation) this._spliceSecondColumn(fWorksheet, this.accountTargetRow, translation, 'suffix');
      }
      this.isAccountsDialogVisible = false;
    },
    resetAccountDialog() {
      this.selectedAccountRow = null;
      this.accountCode = '';
      this.translationCode = 'None';
      this.accountTargetRow = null;
    },
    // Picking a listed account fills the free-text code box; the box is what gets saved.
    onAccountPick() {
      if (this.selectedAccountRow) this.accountCode = this.selectedAccountRow.code;
    },
    // Toolbar / context menu: the account cell is col B of the row the cursor is on, whichever
    // column is actually selected.
    _openAccountDialogFromSelection() {
      if (this.sheetType !== 'cwk') return;
      const fWorksheet = this.univerAPI?.getActiveWorkbook()?.getActiveSheet();
      const fSelection = fWorksheet?.getSelection();
      // Anchor cell, not getActiveRangeList()[0] — on a ctrl-click selection [0] is the oldest
      // range, and on an upward drag getRow() is the top row rather than the cursor's.
      const row = fSelection?.getCurrentCell()?.actualRow
        ?? fSelection?.getActiveRangeList()?.[0]?.getRow();
      if (row == null) return;
      this._openAccountDialog(fWorksheet, row);
    },
    // Open the Account / Translation popup pre-filled from the row's col B "code,translation".
    // The code box keeps the raw account part verbatim (incl. any trailing '#'); the dropdown
    // is matched ignoring '#' so a listed account still shows as selected.
    _openAccountDialog(fWorksheet, row) {
      if (!fWorksheet) return;
      const cell = fWorksheet.getRange(row, CWK_ACCOUNT_COLUMN);
      // Col B is LABEL-only in CWK, so anything else there is legacy content, not an account.
      const component = cell.getCustomMetaData()?.component;
      if (component && component !== 'LABEL') return;

      // A DR/CR formula makes the row a roll-up, not a posting row: col B is closed to both an
      // account and a translation, because the journal load would overwrite that formula.
      const busyColumn = drCrFormulaColumn(fWorksheet, row);
      if (busyColumn !== -1) {
        this.$toast.add({
          severity: 'error', summary: 'Cannot edit column B',
          detail: `Row ${row + 1} has a formula in column ${drCrLabel(busyColumn)}, which the `
            + 'journal load would overwrite. Clear that formula first.',
          life: 5000,
        });
        return;
      }

      const raw = String(cell.getValue() ?? '');
      const commaIndex = raw.indexOf(',');
      const stripHash = (s) => String(s ?? '').trim().replace(/#$/, '');
      const accountPart = (commaIndex === -1 ? raw : raw.slice(0, commaIndex)).trim();
      const translationPart = (commaIndex === -1 ? '' : raw.slice(commaIndex + 1)).trim();
      this.accountCode = accountPart;
      this.selectedAccountRow = accountPart
        ? (this.accounts.find((a) => stripHash(a.code) === stripHash(accountPart)) ?? null)
        : null;
      this.translationCode = this.translationOptions.includes(translationPart) ? translationPart : 'None';
      this.accountTargetRow = row;
      this.isAccountsDialogVisible = true;
    },
    // Col B: prefix=before comma (code), suffix=after (translation).
    _spliceSecondColumn(fWorksheet, row, value, mode) {
      const cell = fWorksheet.getRange(row, CWK_ACCOUNT_COLUMN);
      const current = String(cell.getValue() ?? '');
      const commaIndex = current.indexOf(',');
      const newValue = mode === 'prefix'
        ? (commaIndex === -1 ? `${value},` : value + current.slice(commaIndex))
        : (commaIndex === -1 ? `,${value}` : current.slice(0, commaIndex + 1) + value);
      applyComponent(this.univerAPI, cell, 'LABEL', true);
      cell.setValue(newValue);
    },
    // Context-menu Translation submenu: bulk-applies across whatever col B cells are selected.
    // Skips roll-up rows per-row rather than rejecting the whole selection.
    insertIntoSecondColumn(value, mode) {
      if (this.sheetType !== 'cwk') return;
      const fWorksheet = this.univerAPI?.getActiveWorkbook()?.getActiveSheet();
      const fRangeList = fWorksheet?.getSelection()?.getActiveRangeList() || [];

      let skipped = 0;
      for (const fRange of fRangeList) {
        if (fRange.getColumn() > CWK_ACCOUNT_COLUMN || fRange.getLastColumn() < CWK_ACCOUNT_COLUMN) continue;
        for (let row = fRange.getRow(); row <= fRange.getLastRow(); row++) {
          if (drCrFormulaColumn(fWorksheet, row) !== -1) {
            skipped += 1;
            continue;
          }
          this._spliceSecondColumn(fWorksheet, row, value, mode);
        }
      }
      if (skipped) {
        this.$toast.add({
          severity: 'error', summary: 'Some rows skipped',
          detail: `${skipped} row(s) with a formula in column D (DR) or E (CR) were left unchanged.`,
          life: 5000,
        });
      }
    },

    createMenus() {
      if (this.sheetType !== 'cwk') return;
      if (this._contextMenusInstalled) return;
      this._contextMenusInstalled = true;
      const translationCodes = ['AV', 'FX', 'DT', 'MA'];

      // FORMAT rather than a new group: only items *within* a group sort by `order`, groups
      // render in key-insertion order so a new one lands last. (copy-special is order 0 so <0 order lands above it)
      const CONTEXT_MENU_PATH = ['contextMenu.mainArea', 'contextMenu.format'];

      const submenu = this.univerAPI.createSubmenu({
        id: 'submenu-translation',
        title: 'Translation',
        order: -2,
      });
      translationCodes.forEach((code, index) => {
        submenu.addSubmenu(this.univerAPI.createMenu({
          id: `submenu-translation-${index + 1}`,
          title: code,
          action: () => this.insertIntoSecondColumn(code, 'suffix'),
        }));
      });
      submenu.appendTo(CONTEXT_MENU_PATH);

      this.univerAPI.createMenu({
        id: 'menu-insert-account',
        title: 'Insert Account…',
        order: -1,
        action: () => this._openAccountDialogFromSelection(),
      }).appendTo(CONTEXT_MENU_PATH);

      // Both write col B, so Preview must hide them; the facade builder takes no hidden$.
      this._lockMenuItemsInPreview(['submenu-translation', 'menu-insert-account']);
    },
  },
};
</script>
